import React, { useEffect, useState, createContext, useContext, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { translations, currencySymbols } from "@/translations";
import { getApiUrl } from "@/lib/utils";

const API = getApiUrl();
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
const AUTH_REDIRECT_URL = `${window.location.origin}/dashboard`;

// i18n Context
const I18nContext = createContext(null);

const I18nProvider = ({ children, user, selectedBuilding, viewMode }) => {
  const [language, setLanguage] = useState('tr');
  const [currency, setCurrency] = useState('TRY');
  const [loading, setLoading] = useState(true);

  // Fetch settings when user changes
  // Language: User's personal preference (default: TR)
  // Currency: System-wide (default: TRY)
  useEffect(() => {
    if (user) {
      fetchSettingsForUser();
    } else {
      // No user logged in, use defaults
      setLanguage('tr');
      setCurrency('TRY');
      setLoading(false);
    }
  }, [user?.id, user?.language]);

  const fetchSettingsForUser = async () => {
    try {
      // Fetch user's language + system currency
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setLanguage(response.data.language || 'tr');
      setCurrency(response.data.currency || 'TRY');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Set defaults on error
      setLanguage('tr');
      setCurrency('TRY');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setLanguage(response.data.language || 'tr');
      setCurrency(response.data.currency || 'TRY');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Set defaults on error
      setLanguage('tr');
      setCurrency('TRY');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newLanguage, newCurrency) => {
    try {
      const payload = {};
      if (newLanguage) payload.language = newLanguage;
      if (newCurrency) payload.currency = newCurrency;
      
      await axios.put(`${API}/settings`, payload, { withCredentials: true });
      
      if (newLanguage) setLanguage(newLanguage);
      if (newCurrency) setCurrency(newCurrency);
      
      toast.success(t('settingsUpdated'));
      
      // Refetch to get latest settings
      await fetchSettings();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('currencyChangeNotAllowed'));
      } else {
        toast.error(t('operationFailed'));
      }
    }
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const formatCurrency = (amount) => {
    const symbol = currencySymbols[currency] || '$';
    
    // For TRY (Turkish Lira), use Turkish locale formatting:
    // - Thousands separator: "."
    // - Decimal separator: ","
    // - Example: 104800 => ₺104.800,00
    if (currency === 'TRY') {
      // Handle negative numbers
      const isNegative = amount < 0;
      const absAmount = Math.abs(amount);
      
      // Format with Turkish locale (tr-TR)
      const formattedNumber = absAmount.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return isNegative ? `-${symbol}${formattedNumber}` : `${symbol}${formattedNumber}`;
    }
    
    // For other currencies, keep existing format
    return `${symbol}${amount.toFixed(2)}`;
  };

  return (
    <I18nContext.Provider value={{ language, currency, t, formatCurrency, updateSettings, fetchSettings, loading }}>
      {children}
    </I18nContext.Provider>
  );
};

const useI18n = () => useContext(I18nContext);

// Auth Context
const AuthContext = React.createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);


// Building Context for SuperAdmin
const BuildingContext = React.createContext(null);

const BuildingProvider = ({ children }) => {
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [viewMode, setViewMode] = useState('superadmin'); // 'superadmin' or 'admin'
  
  const selectBuilding = (building) => {
    setSelectedBuilding(building);
  };
  
  const enterAdminMode = () => {
    if (selectedBuilding) {
      setViewMode('admin');
    }
  };
  
  const exitAdminMode = () => {
    setViewMode('superadmin');
  };
  
  const clearBuilding = () => {
    setSelectedBuilding(null);
    setViewMode('superadmin');
  };
  
  return (
    <BuildingContext.Provider value={{ 
      selectedBuilding, 
      viewMode, 
      selectBuilding, 
      enterAdminMode, 
      exitAdminMode, 
      clearBuilding 
    }}>
      {children}
    </BuildingContext.Provider>
  );
};

const useBuilding = () => useContext(BuildingContext);

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Landing Page
const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [showManualLogin, setShowManualLogin] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/app');
    }
  }, [user, navigate]);

  const handleManualLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await axios.post(`${API}/auth/login`, credentials, { withCredentials: true });
      window.location.href = '/app';
    } catch (error) {
      toast.error(t('invalidCredentials'));
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-6">
            <img 
              src="/assets/pandaprop-logo.png" 
              alt={t('landingLogoAlt')}
              className="mx-auto w-24 sm:w-32 md:w-40 h-auto"
            />
          </div>
          
          {/* Subtitle */}
          <h2 className="text-xl sm:text-2xl font-semibold text-teal-600 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('landingSubtitle')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-10 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('landingDescription')}
          </p>
          
          {/* Login Section */}
          {!showManualLogin ? (
            <div className="space-y-3 mb-16">
              <button
                data-testid="login-button"
                onClick={() => setShowManualLogin(true)}
                className="px-10 py-4 bg-teal-600 text-white rounded-full text-lg font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('landingPrimaryCta')}
              </button>
              <p className="text-sm text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('landingLoginHint')}
              </p>
            </div>
          ) : (
            <div className="max-w-md mx-auto bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg mb-16">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('login')}</h2>
              <form onSubmit={handleManualLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-left">{t('username')}</label>
                  <input
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-left">{t('password')}</label>
                  <input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    required
                  />
                </div>
                <div className="flex gap-4 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowManualLogin(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('back')}
                  </button>
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
                  >
                    {loginLoading ? t('loading') : t('login')}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Feature Cards - 6 cards in 3 columns */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-lg font-bold mb-2 text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('featureApartments')}</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('featureApartmentsDesc')}</p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">💳</div>
              <h3 className="text-lg font-bold mb-2 text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('featureDues')}</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('featureDuesDesc')}</p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-bold mb-2 text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('featureExpenses')}</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('featureExpensesDesc')}</p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-lg font-bold mb-2 text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('featureInvestments')}</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('featureInvestmentsDesc')}</p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-lg font-bold mb-2 text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('featureOtherIncome')}</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('featureOtherIncomeDesc')}</p>
            </div>
            <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">📢</div>
              <h3 className="text-lg font-bold mb-2 text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('featureAnnouncements')}</h3>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('featureAnnouncementsDesc')}</p>
            </div>
          </div>
          
          {/* Trust Bullets */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>
            <div className="flex items-center gap-2">
              <span className="text-teal-500">✓</span>
              <span>{t('bulletMultiTenancy')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-teal-500">✓</span>
              <span>{t('bulletAdminResident')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-teal-500">✓</span>
              <span>{t('bulletMonthlyReports')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Session Handler
const SessionHandler = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { t } = useI18n();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (sessionId) {
        try {
          await axios.post(
            `${API}/auth/session`,
            {},
            {
              headers: { 'X-Session-ID': sessionId },
              withCredentials: true
            }
          );
          
          window.history.replaceState(null, '', window.location.pathname);
          await checkAuth();
          navigate('/app', { replace: true });
        } catch (error) {
          console.error('Session error:', error);
          toast.error(t('authenticationFailed'));
          navigate('/');
        }
      } else {
        navigate('/app', { replace: true });
      }
      setProcessing(false);
    };

    handleSession();
  }, [navigate, checkAuth, t]);

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return null;
};

// Dashboard Router
const Dashboard = () => {
  const { user } = useAuth();
  const { viewMode, selectedBuilding } = useBuilding();

  if (!user) return null;

  // SuperAdmin viewing as admin for a selected building
  if (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) {
    return <AdminDashboard buildingId={selectedBuilding.id} isSuperAdminView={true} />;
  }
  
  if (user.role === 'superadmin') {
    return <SuperAdminDashboard />;
  } else if (user.role === 'admin') {
    return <AdminDashboard />;
  } else {
    return <ResidentDashboard />;
  }
};

// SuperAdmin Dashboard
const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { selectedBuilding, selectBuilding, enterAdminMode } = useBuilding();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  useEffect(() => {
    fetchBuildings();
  }, []);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`, { withCredentials: true });
      setBuildings(response.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewAsAdmin = () => {
    if (selectedBuilding) {
      enterAdminMode();
      navigate('/app');
    } else {
      toast.error(t('selectBuilding'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('superAdminPortal')}</h1>
              <p className="text-purple-200 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>{t('manageBuildings')}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm hidden sm:inline" style={{ fontFamily: 'Inter, sans-serif' }}>{user.name}</span>
              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center font-bold text-sm">
                {getInitials(user.name)}
              </div>
              <button
                onClick={logout}
                className="px-3 py-1 text-sm hover:bg-purple-800 rounded transition-colors"
              >
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Building Selector Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('selectBuilding')}:
            </label>
            <select
              value={selectedBuilding?.id || ''}
              onChange={(e) => {
                const building = buildings.find(b => b.id === e.target.value);
                selectBuilding(building);
              }}
              className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <option value="">{t('noBuildingSelected')}</option>
              {buildings.map(building => (
                <option key={building.id} value={building.id}>
                  {building.name} - {building.address}
                </option>
              ))}
            </select>
            <button
              onClick={handleViewAsAdmin}
              disabled={!selectedBuilding}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                selectedBuilding
                  ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('viewAdminPanel')}
            </button>
          </div>
          {selectedBuilding && (
            <div className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('selectedBuilding')}: <span className="font-semibold text-purple-600">{selectedBuilding.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
            <div className="text-sm text-gray-600 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{t('buildings')}</div>
            <div className="text-3xl font-bold text-purple-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{buildings.length}</div>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
            <div className="text-sm text-gray-600 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{t('numberOfFloors')}</div>
            <div className="text-3xl font-bold text-indigo-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {buildings.reduce((sum, b) => sum + b.floors, 0)}
            </div>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
            <div className="text-sm text-gray-600 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{t('numberOfApartments')}</div>
            <div className="text-3xl font-bold text-teal-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {buildings.reduce((sum, b) => sum + b.apartments, 0)}
            </div>
          </div>
        </div>

        {/* Buildings Management Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('buildingManagement')}
            </h2>
            <button
              onClick={() => navigate('/buildings')}
              className="px-6 py-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-all duration-300 transform hover:-translate-y-1 shadow-lg font-semibold"
            >
              {t('manageBuildings')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {buildings.slice(0, 6).map((building) => (
              <div key={building.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                <h3 className="font-bold text-lg mb-2">{building.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{building.address}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{building.floors} {t('numberOfFloors')}</span>
                  <span className="text-gray-500">{building.apartments} {t('apartments')}</span>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-gray-500">{t('buildingAdmin')}</div>
                  <div className="text-sm font-semibold">{building.admin_name}</div>
                </div>
              </div>
            ))}
          </div>

          {buildings.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {t('noBuildings')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard - Continued in next part due to size
const AdminDashboard = ({ buildingId = null, isSuperAdminView = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, formatCurrency } = useI18n();
  const { selectedBuilding, exitAdminMode } = useBuilding();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Monthly Financial Summary state (same as Resident portal)
  const [fees, setFees] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [expenseMonth, setExpenseMonth] = useState(new Date().getMonth());
  const [expenseYear, setExpenseYear] = useState(new Date().getFullYear());
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth());
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportAidatIncome, setReportAidatIncome] = useState(0);
  const [loadingAidatIncome, setLoadingAidatIncome] = useState(false);
  
  // Monthly summary from backend (reconciled with Operating Ledger)
  const [monthlySummaryData, setMonthlySummaryData] = useState(null);
  const [loadingMonthlySummary, setLoadingMonthlySummary] = useState(false);
  
  // Quick Month/Year Picker state for Monthly Financial Summary
  const [showSummaryMonthPicker, setShowSummaryMonthPicker] = useState(false);
  const [summaryPickerMonth, setSummaryPickerMonth] = useState(new Date().getMonth());
  const [summaryPickerYear, setSummaryPickerYear] = useState(new Date().getFullYear());
  
  // Quick Month/Year Picker state for Detailed Report Modal
  const [showReportMonthPicker, setShowReportMonthPicker] = useState(false);
  const [reportPickerMonth, setReportPickerMonth] = useState(new Date().getMonth());
  const [reportPickerYear, setReportPickerYear] = useState(new Date().getFullYear());

  // Get month names from translations
  const getMonthName = (monthIndex) => {
    return t(`months.${monthIndex + 1}`);
  };

  useEffect(() => {
    fetchStats();
    fetchFinancialData();
  }, [buildingId]);

  // Fetch aidat income when report modal month changes (for itemized breakdown)
  useEffect(() => {
    if (showDetailedReport) {
      fetchReportAidatIncome(reportMonth, reportYear);
      fetchReportSummary(reportMonth, reportYear);
    }
  }, [showDetailedReport, reportMonth, reportYear, buildingId]);

  // State for detailed report summary (same as monthly summary for consistency)
  const [reportSummaryData, setReportSummaryData] = useState(null);
  const [loadingReportSummary, setLoadingReportSummary] = useState(false);

  // Fetch monthly summary for detailed report (same endpoint as Monthly Financial Summary)
  const fetchReportSummary = async (month, year) => {
    setLoadingReportSummary(true);
    try {
      const buildingQuery = buildingId ? `&building_id=${buildingId}` : '';
      const response = await axios.get(
        `${API}/admin/monthly-summary?month=${month + 1}&year=${year}${buildingQuery}`,
        { withCredentials: true }
      );
      setReportSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching report summary:', error);
      setReportSummaryData(null);
    } finally {
      setLoadingReportSummary(false);
    }
  };

  const fetchReportAidatIncome = async (month, year) => {
    try {
      setLoadingAidatIncome(true);
      // Note: API expects 1-based month (1-12), but JS uses 0-based (0-11)
      const apiMonth = month + 1;
      const url = buildingId 
        ? `${API}/admin/aidat-income?month=${apiMonth}&year=${year}&building_id=${buildingId}`
        : `${API}/admin/aidat-income?month=${apiMonth}&year=${year}`;
      const response = await axios.get(url, { withCredentials: true });
      // Use total_aidat_income only (excludes other payments like kapıcı) - matches Operating Ledger
      setReportAidatIncome(response.data.total_aidat_income || 0);
    } catch (error) {
      console.error('Error fetching aidat income:', error);
      setReportAidatIncome(0);
    } finally {
      setLoadingAidatIncome(false);
    }
  };

  const fetchStats = async () => {
    try {
      const url = buildingId 
        ? `${API}/admin/dashboard?building_id=${buildingId}`
        : `${API}/admin/dashboard`;
      const response = await axios.get(url, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialData = async () => {
    try {
      const buildingQuery = buildingId ? `?building_id=${buildingId}` : '';
      const [feesRes, expensesRes, investmentsRes, otherIncomeRes] = await Promise.all([
        axios.get(`${API}/maintenance-fees${buildingQuery}`, { withCredentials: true }),
        axios.get(`${API}/expenses${buildingQuery}`, { withCredentials: true }),
        axios.get(`${API}/investments${buildingQuery}`, { withCredentials: true }),
        axios.get(`${API}/other-income${buildingQuery}`, { withCredentials: true })
      ]);
      setFees(feesRes.data);
      setExpenses(expensesRes.data);
      setInvestments(investmentsRes.data);
      setOtherIncome(otherIncomeRes.data);
    } catch (error) {
      console.error('Error fetching financial data:', error);
    }
  };

  // Fetch monthly summary from backend (reconciled with Operating Ledger)
  const fetchMonthlySummary = async (targetMonth, targetYear) => {
    setLoadingMonthlySummary(true);
    try {
      const buildingQuery = buildingId ? `&building_id=${buildingId}` : '';
      const response = await axios.get(
        `${API}/admin/monthly-summary?month=${targetMonth + 1}&year=${targetYear}${buildingQuery}`,
        { withCredentials: true }
      );
      setMonthlySummaryData(response.data);
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
      setMonthlySummaryData(null);
    } finally {
      setLoadingMonthlySummary(false);
    }
  };

  // Fetch monthly summary when expenseMonth/expenseYear changes
  useEffect(() => {
    if (user) {
      fetchMonthlySummary(expenseMonth, expenseYear);
    }
  }, [expenseMonth, expenseYear, buildingId, user]);

  const changeExpenseMonth = (delta) => {
    let newMonth = expenseMonth + delta;
    let newYear = expenseYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setExpenseMonth(newMonth);
    setExpenseYear(newYear);
  };

  const changeReportMonth = (delta) => {
    let newMonth = reportMonth + delta;
    let newYear = reportYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setReportMonth(newMonth);
    setReportYear(newYear);
  };

  // Helper function to filter paid fees by payment date (not period)
  // This ensures Monthly Financial Summary matches Operating Ledger behavior
  const filterFeesByPaymentDate = (feesList, targetMonth, targetYear) => {
    return feesList.filter(f => {
      if (f.status !== 'paid' || !f.paid_date) return false;
      try {
        const paidDate = new Date(f.paid_date);
        if (isNaN(paidDate.getTime())) return false;
        return paidDate.getMonth() === targetMonth && paidDate.getFullYear() === targetYear;
      } catch {
        return false;
      }
    });
  };

  // Calculate income and expenses for the selected expense month/year
  // IMPORTANT: Maintenance fee income is based on PAYMENT DATE, not fee period
  const expenseMonthFees = filterFeesByPaymentDate(fees, expenseMonth, expenseYear);
  const expenseMonthMaintenanceIncome = expenseMonthFees.reduce((sum, fee) => sum + fee.amount, 0);
  
  const expenseMonthOtherIncome = otherIncome.filter(income => {
    const incomeDate = new Date(income.date);
    return incomeDate.getMonth() === expenseMonth && incomeDate.getFullYear() === expenseYear;
  });
  const expenseMonthOtherIncomeTotal = expenseMonthOtherIncome.reduce((sum, income) => sum + income.amount, 0);
  
  const expenseMonthExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.date);
    return expenseDate.getMonth() === expenseMonth && expenseDate.getFullYear() === expenseYear;
  });
  const expenseMonthExpensesTotal = expenseMonthExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  
  const expenseMonthInvestments = investments.filter(investment => {
    if (!investment.start_date) return false;
    const startDate = new Date(investment.start_date);
    return startDate.getMonth() === expenseMonth && startDate.getFullYear() === expenseYear;
  });
  const expenseMonthInvestmentsTotal = expenseMonthInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  
  const totalIncome = expenseMonthMaintenanceIncome + expenseMonthOtherIncomeTotal;
  const totalExpense = expenseMonthExpensesTotal + expenseMonthInvestmentsTotal;

  // Calculate ending balance for a specific month (recursive)
  // IMPORTANT: Maintenance fee income is based on PAYMENT DATE, not fee period
  const calculateMonthBalance = (targetMonth, targetYear, visitedMonths = new Set()) => {
    const monthKey = `${targetYear}-${targetMonth}`;
    if (visitedMonths.has(monthKey)) return 0;
    visitedMonths.add(monthKey);
    
    // Filter fees by payment date (when paid), not by period
    const monthMaintenanceFees = filterFeesByPaymentDate(fees, targetMonth, targetYear);
    const monthMaintenanceIncome = monthMaintenanceFees.reduce((sum, fee) => sum + fee.amount, 0);
    
    const monthOtherIncome = otherIncome.filter(income => {
      const incomeDate = new Date(income.date);
      return incomeDate.getMonth() === targetMonth && incomeDate.getFullYear() === targetYear;
    });
    const monthOtherIncomeTotal = monthOtherIncome.reduce((sum, income) => sum + income.amount, 0);
    
    const monthExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getMonth() === targetMonth && expenseDate.getFullYear() === targetYear;
    });
    const monthExpensesTotal = monthExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    
    const monthInvestments = investments.filter(investment => {
      if (!investment.start_date) return false;
      const startDate = new Date(investment.start_date);
      return startDate.getMonth() === targetMonth && startDate.getFullYear() === targetYear;
    });
    const monthInvestmentsTotal = monthInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    
    const monthIncomeTotal = monthMaintenanceIncome + monthOtherIncomeTotal;
    const monthExpenseTotal = monthExpensesTotal + monthInvestmentsTotal;
    
    // Find earliest date based on payment dates for fees, not period
    const allDates = [
      ...fees.filter(f => f.status === 'paid' && f.paid_date).map(f => new Date(f.paid_date)),
      ...otherIncome.map(i => new Date(i.date)),
      ...expenses.map(e => new Date(e.date)),
      ...investments.filter(i => i.start_date).map(i => new Date(i.start_date))
    ].filter(d => !isNaN(d.getTime()));
    
    const earliestDate = allDates.length > 0 
      ? new Date(Math.min(...allDates.map(d => d.getTime())))
      : new Date();
    const earliestMonth = earliestDate.getMonth();
    const earliestYear = earliestDate.getFullYear();
    
    if (targetYear < earliestYear || (targetYear === earliestYear && targetMonth <= earliestMonth)) {
      return monthIncomeTotal - monthExpenseTotal;
    }
    
    const prevMonth = targetMonth === 0 ? 11 : targetMonth - 1;
    const prevYear = targetMonth === 0 ? targetYear - 1 : targetYear;
    const carryoverFromPrev = calculateMonthBalance(prevMonth, prevYear, visitedMonths);
    
    return carryoverFromPrev + monthIncomeTotal - monthExpenseTotal;
  };
  
  const calculateCarryover = (month, year) => {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    
    const allDates = [
      ...fees.filter(f => f.status === 'paid').map(f => new Date(f.year, f.month - 1)),
      ...otherIncome.map(i => new Date(i.date)),
      ...expenses.map(e => new Date(e.date)),
      ...investments.filter(i => i.start_date).map(i => new Date(i.start_date))
    ].filter(d => !isNaN(d.getTime()));
    
    if (allDates.length === 0) return 0;
    
    const earliestDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const earliestMonth = earliestDate.getMonth();
    const earliestYear = earliestDate.getFullYear();
    
    if (year < earliestYear || (year === earliestYear && month <= earliestMonth)) {
      return 0;
    }
    
    return calculateMonthBalance(prevMonth, prevYear);
  };
  
  const carryover = calculateCarryover(expenseMonth, expenseYear);
  const currentBalance = carryover + totalIncome - totalExpense;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const handleBackToSuperAdmin = () => {
    exitAdminMode();
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {isSuperAdminView && selectedBuilding ? t('managingBuilding') : t('adminDashboard')}
              </h1>
              {isSuperAdminView && selectedBuilding && (
                <p className="text-sm text-purple-600 font-semibold mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {selectedBuilding.name} - {t('viewingAsAdmin')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              {isSuperAdminView && (
                <button
                  onClick={handleBackToSuperAdmin}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-semibold"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('backToSuperAdmin')}
                </button>
              )}
              <span className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{user.name}</span>
              <button
                data-testid="logout-button"
                onClick={logout}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Primary Action Button - View Operating Ledger */}
        <div className="mb-4">
          <button
            data-testid="btn-operating-ledger"
            onClick={() => navigate('/operating-ledger')}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-2xl py-5 px-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">📒</span>
              <span className="text-xl font-bold">{t('viewOperatingLedger')}</span>
            </div>
          </button>
        </div>

        {/* Secondary Action Button - Bank Statements */}
        <div className="mb-8">
          <button
            data-testid="btn-bank-statements"
            onClick={() => navigate('/bank-statements')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-2xl py-4 px-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-xl">🏦</span>
              <span className="text-lg font-bold">{t('bankStatementsNav')}</span>
            </div>
          </button>
        </div>

        {/* Aylık Mali Özet Section - Monthly Financial Summary */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg mb-8">
          {/* Section Header with Month Navigation */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('monthlySummary')}</h2>
            <div className="flex items-center gap-2 sm:gap-3 bg-gray-100 rounded-full px-2 py-1 w-full sm:w-auto justify-center">
              <button 
                onClick={() => changeExpenseMonth(-1)}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-white hover:shadow transition-all text-gray-600 text-sm"
                data-testid="summary-prev-month"
              >
                ←
              </button>
              <button 
                onClick={() => {
                  setSummaryPickerMonth(expenseMonth);
                  setSummaryPickerYear(expenseYear);
                  setShowSummaryMonthPicker(true);
                }}
                className="text-xs sm:text-sm font-semibold text-gray-700 min-w-[100px] sm:min-w-[120px] text-center hover:bg-white hover:shadow px-2 py-1 rounded-full transition-all cursor-pointer"
                data-testid="summary-month-picker-btn"
              >
                {getMonthName(expenseMonth)} {expenseYear}
              </button>
              <button 
                onClick={() => changeExpenseMonth(1)}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-white hover:shadow transition-all text-gray-600 text-sm"
                data-testid="summary-next-month"
              >
                →
              </button>
            </div>
          </div>
          
          {/* 4-Box Summary Grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
            {/* Carryover - Secondary */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-2 sm:p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('carryover')}</div>
              </div>
              <div className="text-xs text-gray-400 mb-1 hidden sm:block">{t('carryoverFromPrevMonth')}</div>
              <div className="text-sm sm:text-lg font-semibold text-gray-600 break-all">
                {loadingMonthlySummary ? '...' : (monthlySummaryData?.carryover >= 0 ? '+' : '')}{loadingMonthlySummary ? '' : formatCurrency(monthlySummaryData?.carryover || 0)}
              </div>
            </div>

            {/* Income - Secondary */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-2 sm:p-3 border border-green-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-green-600 uppercase tracking-wide">{t('income')}</div>
                <div className="text-xs text-green-500">+</div>
              </div>
              <div className="text-sm sm:text-lg font-semibold text-green-600 break-all">
                {loadingMonthlySummary ? '...' : formatCurrency(monthlySummaryData?.total_income || 0)}
              </div>
            </div>

            {/* Expenses - Secondary */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-2 sm:p-3 border border-red-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-red-600 uppercase tracking-wide">{t('expenses')}</div>
                <div className="text-xs text-red-500">−</div>
              </div>
              <div className="text-sm sm:text-lg font-semibold text-red-600 break-all">
                {loadingMonthlySummary ? '...' : formatCurrency(monthlySummaryData?.total_expenses || 0)}
              </div>
            </div>

            {/* Balance - PRIMARY (Most Prominent) */}
            <div className={`bg-gradient-to-br rounded-xl p-2 sm:p-3 border-2 shadow-md ${
              (monthlySummaryData?.balance || 0) >= 0 
                ? 'from-blue-100 to-blue-200 border-blue-400' 
                : 'from-red-100 to-red-200 border-red-400'
            }`}>
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs font-bold uppercase tracking-wide ${
                  (monthlySummaryData?.balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'
                }`}>
                  {t('balance')}
                </div>
                <div className={`text-xs font-bold ${(monthlySummaryData?.balance || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  =
                </div>
              </div>
              <div className={`text-lg sm:text-2xl font-bold break-all ${(monthlySummaryData?.balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                {loadingMonthlySummary ? '...' : ((monthlySummaryData?.balance || 0) >= 0 ? '+' : '')}{loadingMonthlySummary ? '' : formatCurrency(monthlySummaryData?.balance || 0)}
              </div>
            </div>
          </div>

          {/* Detailed Report Button */}
          <button
            onClick={() => {
              setReportMonth(expenseMonth);
              setReportYear(expenseYear);
              setShowDetailedReport(true);
            }}
            className="w-full py-2 sm:py-2.5 px-4 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg border border-teal-200 text-xs sm:text-sm font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <span>📊</span>
            {t('viewDetailedReport')}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <button
            data-testid="nav-apartments"
            onClick={() => navigate('/apartments')}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">🏢</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('apartments')}</h3>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('manageApartments')}</p>
          </button>
          <button
            data-testid="nav-fees"
            onClick={() => navigate('/fees')}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('maintenanceFees')}</h3>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('trackPayments')}</p>
          </button>
          <button
            data-testid="nav-expenses"
            onClick={() => navigate('/expenses')}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('expenses')}</h3>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('recordExpenses')}</p>
          </button>
          <button
            data-testid="nav-announcements"
            onClick={() => navigate('/announcements')}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">📢</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('announcements')}</h3>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('postUpdates')}</p>
          </button>
          <button
            data-testid="nav-investments"
            onClick={() => navigate('/investments')}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">🏗️</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('investments')}</h3>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('manageInvestments')}</p>
          </button>
          <button
            data-testid="nav-other-income"
            onClick={() => navigate('/other-income')}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">💵</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('otherIncome')}</h3>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('manageOtherIncome')}</p>
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="bg-purple-600 text-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">⚙️</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('settings')}</h3>
            <p className="text-purple-100 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('language')} & {t('currency')}</p>
          </button>
          <button
            onClick={() => navigate('/users')}
            className="bg-indigo-600 text-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">👥</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('users')}</h3>
            <p className="text-indigo-100 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('manageUsers')}</p>
          </button>
          <button
            data-testid="nav-whatsapp"
            id="whatsapp-nav-btn"
            onClick={() => navigate('/whatsapp')}
            className="bg-green-600 text-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left"
          >
            <div className="text-4xl mb-4">
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.963 7.963 0 01-4.106-1.138l-.294-.176-2.866.852.852-2.866-.176-.294A7.963 7.963 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/></svg>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('waPageTitle') || 'WhatsApp'}</h3>
            <p className="text-green-100 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('waNavDesc') || 'Send notifications'}</p>
          </button>
        </div>
      </div>

      {/* Admin Detailed Financial Report Modal */}
      {showDetailedReport && (() => {
        // USE HEADLINE TOTALS FROM SAME API AS MONTHLY SUMMARY (for consistency)
        // This ensures carryover, total income, total expense, and balance match exactly
        const reportCarryover = reportSummaryData?.carryover || 0;
        const reportTotalIncome = reportSummaryData?.total_income || 0;
        const reportTotalExpenses = reportSummaryData?.total_expenses || 0;
        const reportBalance = reportSummaryData?.balance || 0;
        
        // Itemized aidat income (for breakdown display, not headline total)
        const maintenanceFeeIncome = reportAidatIncome;
        
        // Get itemized other income for the selected month (for breakdown display)
        const reportMonthOtherIncomeItems = otherIncome.filter(income => {
          const incomeDate = new Date(income.date);
          return incomeDate.getMonth() === reportMonth && incomeDate.getFullYear() === reportYear;
        });
        
        const totalOtherIncome = reportMonthOtherIncomeItems.reduce((sum, income) => sum + income.amount, 0);
        
        // Get itemized expenses for selected report month/year (for breakdown display)
        const reportMonthExpenseItems = expenses.filter(expense => {
          const expenseDate = new Date(expense.date);
          return expenseDate.getMonth() === reportMonth && expenseDate.getFullYear() === reportYear;
        });
        
        const totalRegularExpenses = reportMonthExpenseItems.reduce((sum, exp) => sum + exp.amount, 0);
        
        // Get itemized investments for the selected month (for breakdown display)
        const reportMonthInvestmentItems = investments.filter(investment => {
          if (!investment.start_date) return false;
          const startDate = new Date(investment.start_date);
          return startDate.getMonth() === reportMonth && startDate.getFullYear() === reportYear;
        });
        
        const totalInvestments = reportMonthInvestmentItems.reduce((sum, inv) => sum + (inv.amount || 0), 0);
        
        // Pool income from the API (difference between total income and itemized aidat + other income)
        const poolIncome = reportSummaryData?.pool_income || 0;
        const otherPaymentIncome = reportSummaryData?.other_payment_income || 0;
        
        // Helper to translate other income category
        const translateOtherIncomeCategory = (category) => {
          const categoryMap = {
            commonAreaRental: t('commonAreaRental'),
            parkingFees: t('parkingFees'),
            facilityUsageFees: t('facilityUsageFees'),
            penalties: t('penalties'),
            keyCardFees: t('keyCardFees'),
            otherCategory: t('otherCategory')
          };
          return categoryMap[category] || category;
        };
        
        // Helper to translate expense category
        const translateExpenseCategory = (categoryKey) => {
          if (!categoryKey) return '';
          const translatedKey = t(`expenseCategories.${categoryKey}`);
          if (translatedKey === `expenseCategories.${categoryKey}`) {
            return categoryKey;
          }
          return translatedKey;
        };
        
        // Helper to translate expense subcategory
        const translateExpenseSubcategory = (categoryKey, subcategoryKey) => {
          if (!categoryKey || !subcategoryKey) return '';
          const translatedKey = t(`expenseSubcategories.${categoryKey}.${subcategoryKey}`);
          if (translatedKey === `expenseSubcategories.${categoryKey}.${subcategoryKey}`) {
            return subcategoryKey;
          }
          return translatedKey;
        };
        
        // Helper to translate investment category
        const translateInvestmentCategory = (categoryKey) => {
          if (!categoryKey) return '';
          const translatedKey = t(`investmentCategories.${categoryKey}`);
          if (translatedKey === `investmentCategories.${categoryKey}`) {
            return categoryKey;
          }
          return translatedKey;
        };
        
        // Helper to translate investment subcategory
        const translateInvestmentSubcategory = (categoryKey, subcategoryKey) => {
          if (!categoryKey || !subcategoryKey) return '';
          const translatedKey = t(`investmentSubcategories.${categoryKey}.${subcategoryKey}`);
          if (translatedKey === `investmentSubcategories.${categoryKey}.${subcategoryKey}`) {
            return subcategoryKey;
          }
          return translatedKey;
        };
        
        // Format date helper
        const formatDate = (dateStr) => {
          if (!dateStr) return '-';
          const date = new Date(dateStr);
          return date.toLocaleDateString();
        };
        
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto my-4 sm:my-8">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {t('detailedReportTitle')}
                </h2>
                <button 
                  onClick={() => setShowDetailedReport(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>

              {/* Month Selector */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <button 
                    onClick={() => changeReportMonth(-1)}
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                    data-testid="report-prev-month"
                  >
                    ←
                  </button>
                  <button 
                    onClick={() => {
                      setReportPickerMonth(reportMonth);
                      setReportPickerYear(reportYear);
                      setShowReportMonthPicker(true);
                    }}
                    className="text-center font-bold text-gray-800 text-base sm:text-lg min-w-[140px] hover:bg-gray-200 px-3 py-1 rounded-full transition-all cursor-pointer"
                    data-testid="report-month-picker-btn"
                  >
                    {getMonthName(reportMonth)} {reportYear}
                  </button>
                  <button 
                    onClick={() => changeReportMonth(1)}
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                    data-testid="report-next-month"
                  >
                    →
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6">
                {/* INCOME DETAILS SECTION */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t('incomeSectionTitle')}
                  </h3>
                  
                  {/* Assigned Aidat Income - Payments assigned to specific apartments */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('assignedAidatIncomeLabel')}</h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-gray-800">{t('assignedAidatIncomeLabel')}</span>
                          <p className="text-xs text-gray-500 mt-1">{t('assignedAidatIncomeNote')}</p>
                        </div>
                        <span className="font-bold text-green-600 text-lg">{loadingAidatIncome ? '...' : formatCurrency(maintenanceFeeIncome)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Pending Pool Income - Payments not yet assigned to any apartment */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('pendingPoolIncomeLabel')}</h4>
                    <div className={`border rounded-lg p-4 ${poolIncome > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-gray-800">{t('pendingPoolIncomeLabel')}</span>
                          <p className="text-xs text-gray-500 mt-1">{t('pendingPoolIncomeNote')}</p>
                        </div>
                        <span className={`font-bold text-lg ${poolIncome > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {loadingReportSummary ? '...' : formatCurrency(poolIncome)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Other Payment Income - Payments for other fees like "Kapıcı tazminatı" */}
                  {otherPaymentIncome > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('otherPaymentIncomeLabel') || 'Diğer Ödemeler Geliri'}</h4>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-semibold text-gray-800">{t('otherPaymentIncomeLabel') || 'Diğer Ödemeler Geliri'}</span>
                            <p className="text-xs text-gray-500 mt-1">{t('otherPaymentIncomeNote') || 'Kapıcı tazminatı vb. daire ödemeleri'}</p>
                          </div>
                          <span className="font-bold text-blue-600 text-lg">{loadingReportSummary ? '...' : formatCurrency(otherPaymentIncome)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Other Income - Itemized List */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('otherIncomeSectionTitle')}</h4>
                    {reportMonthOtherIncomeItems.length > 0 ? (
                      <div className="space-y-2">
                        {reportMonthOtherIncomeItems.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{translateOtherIncomeCategory(item.category)}</div>
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">{formatDate(item.date)}</p>
                              </div>
                              <span className="font-semibold text-green-600">{formatCurrency(item.amount)}</span>
                            </div>
                          </div>
                        ))}
                        {/* Other Income Total */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex justify-between items-center">
                          <span className="font-semibold text-gray-700">{t('otherIncomeTotalLabel')}</span>
                          <span className="font-bold text-green-600">{formatCurrency(totalOtherIncome)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        {t('noOtherIncomeThisMonth')}
                      </div>
                    )}
                  </div>
                </div>

                {/* EXPENSE DETAILS SECTION */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t('expensesSectionTitle')}
                  </h3>
                  
                  {/* Expenses - Itemized List */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('expenseItemsSectionTitle')}</h4>
                    {reportMonthExpenseItems.length > 0 ? (
                      <div className="space-y-2">
                        {reportMonthExpenseItems.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{translateExpenseCategory(item.category)}</div>
                                {item.subcategory && (
                                  <p className="text-sm text-gray-600">{translateExpenseSubcategory(item.category, item.subcategory)}</p>
                                )}
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">{formatDate(item.date)}</p>
                              </div>
                              <span className="font-semibold text-red-600">{formatCurrency(item.amount)}</span>
                            </div>
                          </div>
                        ))}
                        {/* Expenses Total */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-center">
                          <span className="font-semibold text-gray-700">{t('expensesTotalLabel')}</span>
                          <span className="font-bold text-red-600">{formatCurrency(totalRegularExpenses)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        {t('noExpensesThisMonth')}
                      </div>
                    )}
                  </div>
                  
                  {/* Investments - Itemized List */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('investmentsSectionTitle')}</h4>
                    {reportMonthInvestmentItems.length > 0 ? (
                      <div className="space-y-2">
                        {reportMonthInvestmentItems.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{translateInvestmentCategory(item.category)}</div>
                                {item.subcategory && (
                                  <p className="text-sm text-gray-600">{translateInvestmentSubcategory(item.category, item.subcategory)}</p>
                                )}
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">{formatDate(item.start_date)}</p>
                              </div>
                              <span className="font-semibold text-red-600">
                                {item.amount ? formatCurrency(item.amount) : '—'}
                              </span>
                            </div>
                          </div>
                        ))}
                        {/* Investments Total */}
                        {totalInvestments > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-center">
                            <span className="font-semibold text-gray-700">{t('investmentsTotalLabel')}</span>
                            <span className="font-bold text-red-600">{formatCurrency(totalInvestments)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        {t('noInvestmentsThisMonth')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Quick Month/Year Picker Modal - Monthly Financial Summary */}
      {showSummaryMonthPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="summary-month-picker-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('selectMonthYear') || 'Select Month/Year'}
            </h3>
            
            {/* Year Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('year') || 'Year'}</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSummaryPickerYear(prev => prev - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  ←
                </button>
                <span className="flex-1 text-center text-xl font-bold text-gray-800">{summaryPickerYear}</span>
                <button
                  onClick={() => setSummaryPickerYear(prev => prev + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  →
                </button>
              </div>
            </div>
            
            {/* Month Selection Grid - Note: expenseMonth is 0-based */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('month') || 'Month'}</label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((month) => (
                  <button
                    key={month}
                    onClick={() => setSummaryPickerMonth(month)}
                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-colors ${
                      summaryPickerMonth === month
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`summary-month-picker-${month}`}
                  >
                    {getMonthName(month).substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Selected Preview */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
              <span className="text-blue-700 font-semibold">
                {getMonthName(summaryPickerMonth)} {summaryPickerYear}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowSummaryMonthPicker(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                data-testid="summary-month-picker-cancel"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  setExpenseMonth(summaryPickerMonth);
                  setExpenseYear(summaryPickerYear);
                  setShowSummaryMonthPicker(false);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                data-testid="summary-month-picker-apply"
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Month/Year Picker Modal - Detailed Report */}
      {showReportMonthPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="report-month-picker-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('selectMonthYear') || 'Select Month/Year'}
            </h3>
            
            {/* Year Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('year') || 'Year'}</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setReportPickerYear(prev => prev - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  ←
                </button>
                <span className="flex-1 text-center text-xl font-bold text-gray-800">{reportPickerYear}</span>
                <button
                  onClick={() => setReportPickerYear(prev => prev + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  →
                </button>
              </div>
            </div>
            
            {/* Month Selection Grid - Note: reportMonth is 0-based */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('month') || 'Month'}</label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((month) => (
                  <button
                    key={month}
                    onClick={() => setReportPickerMonth(month)}
                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-colors ${
                      reportPickerMonth === month
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`report-month-picker-${month}`}
                  >
                    {getMonthName(month).substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Selected Preview */}
            <div className="mb-4 p-3 bg-indigo-50 rounded-lg text-center">
              <span className="text-indigo-700 font-semibold">
                {getMonthName(reportPickerMonth)} {reportPickerYear}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowReportMonthPicker(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                data-testid="report-month-picker-cancel"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  setReportMonth(reportPickerMonth);
                  setReportYear(reportPickerYear);
                  setShowReportMonthPicker(false);
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                data-testid="report-month-picker-apply"
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Resident Dashboard
const ResidentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, formatCurrency, language } = useI18n();
  const [stats, setStats] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [fees, setFees] = useState([]);
  const [userFees, setUserFees] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expenseMonth, setExpenseMonth] = useState(new Date().getMonth());
  const [expenseYear, setExpenseYear] = useState(new Date().getFullYear());
  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isPaymentFocused, setIsPaymentFocused] = useState(false);
  const [cumulativeData, setCumulativeData] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  const [aptContextList, setAptContextList] = useState([]);
  const [aptContextTotal, setAptContextTotal] = useState(0);
  const [selectedAptId, setSelectedAptId] = useState(null);
  const [residentMonthlySummary, setResidentMonthlySummary] = useState(null);
  const [loadingResidentSummary, setLoadingResidentSummary] = useState(false);
  const [residentReportSummary, setResidentReportSummary] = useState(null);
  const [loadingResidentReportSummary, setLoadingResidentReportSummary] = useState(false);
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth());
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportAidatIncome, setReportAidatIncome] = useState(0);
  const [loadingAidatIncome, setLoadingAidatIncome] = useState(false);
  // Month/Year Picker state for Resident Monthly Summary
  const [showResidentSummaryPicker, setShowResidentSummaryPicker] = useState(false);
  const [resSummaryPickerMonth, setResSummaryPickerMonth] = useState(new Date().getMonth());
  const [resSummaryPickerYear, setResSummaryPickerYear] = useState(new Date().getFullYear());
  // Month/Year Picker state for Resident Report Detail
  const [showResidentReportPicker, setShowResidentReportPicker] = useState(false);
  const [resReportPickerMonth, setResReportPickerMonth] = useState(new Date().getMonth());
  const [resReportPickerYear, setResReportPickerYear] = useState(new Date().getFullYear());
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Get month names from translations
  const getMonthName = (monthIndex) => {
    return t(`months.${monthIndex + 1}`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize expandedYears and paymentAmount when payment status modal opens
  useEffect(() => {
    if (showPaymentStatus) {
      // Set current year expanded, others collapsed
      const currentYr = new Date().getFullYear();
      const years = [...new Set(userFees.map(f => f.year))];
      const ey = {};
      years.forEach(y => { ey[y] = y === currentYr; });
      setExpandedYears(ey);
    }
  }, [showPaymentStatus, userFees]);

  const fetchData = async () => {
    try {
      // First, get the dashboard data which includes announcements filtered by building
      const dashboardRes = await axios.get(`${API}/resident/dashboard`, { withCredentials: true });
      setStats(dashboardRes.data);

      // Owner/Tenant context for this resident (per-unit role + debts)
      try {
        const ctxRes = await axios.get(`${API}/resident/apartment-context`, { withCredentials: true });
        const apts = ctxRes.data?.apartments || [];
        setAptContextList(apts);
        setAptContextTotal(ctxRes.data?.own_debt_total || 0);
        setSelectedAptId((prev) => prev || ctxRes.data?.primary?.apartment_id || user.apartment_id || null);
      } catch (ctxErr) {
        console.error('Apartment context fetch failed:', ctxErr);
      }
      
      // Fetch maintenance fees for the resident (building-wide; per-unit slice handled by effect)
      const feesRes = await axios.get(`${API}/maintenance-fees`, { withCredentials: true });
      setFees(feesRes.data);
      
      // Fetch building-level financial data (expenses, investments, other income)
      // These are visible to all users in the building for transparency
      const [expensesRes, investmentsRes, otherIncomeRes] = await Promise.all([
        axios.get(`${API}/expenses`, { withCredentials: true }),
        axios.get(`${API}/investments`, { withCredentials: true }),
        axios.get(`${API}/other-income`, { withCredentials: true })
      ]);
      
      setExpenses(expensesRes.data);
      setInvestments(investmentsRes.data);
      setOtherIncome(otherIncomeRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Load the selected unit's fee slice + cumulative whenever the unit (or fees) changes.
  // Supports multi-unit owners switching between their apartments.
  useEffect(() => {
    const aptId = selectedAptId || user.apartment_id;
    if (!aptId || !fees || fees.length === 0) return;
    const apartmentFees = fees.filter((fee) => fee.apartment_id === aptId);
    setUserFees(apartmentFees);
    let cancelled = false;
    (async () => {
      try {
        const years = [...new Set(apartmentFees.map((f) => f.year))];
        const currentYear = new Date().getFullYear();
        if (!years.includes(currentYear)) years.push(currentYear);
        const allCumulativeData = [];
        for (const year of years) {
          try {
            const cumulRes = await axios.get(`${API}/maintenance-fees/cumulative?apartment_id=${aptId}&year=${year}`, { withCredentials: true });
            if (cumulRes.data && cumulRes.data.fees) allCumulativeData.push(...cumulRes.data.fees);
          } catch (yearErr) {
            console.error(`Cumulative data fetch failed for year ${year}:`, yearErr);
          }
        }
        if (!cancelled) setCumulativeData(allCumulativeData);
      } catch (e) {
        console.error('Cumulative data fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAptId, fees, user.apartment_id]);

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const changeExpenseMonth = (delta) => {
    let newMonth = expenseMonth + delta;
    let newYear = expenseYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setExpenseMonth(newMonth);
    setExpenseYear(newYear);
  };

  // Fetch monthly summary from backend (same endpoint as Admin - reconciled with Operating Ledger)
  const fetchResidentMonthlySummary = async (targetMonth, targetYear) => {
    setLoadingResidentSummary(true);
    try {
      const response = await axios.get(
        `${API}/admin/monthly-summary?month=${targetMonth + 1}&year=${targetYear}`,
        { withCredentials: true }
      );
      setResidentMonthlySummary(response.data);
    } catch (error) {
      console.error('Error fetching resident monthly summary:', error);
      setResidentMonthlySummary(null);
    } finally {
      setLoadingResidentSummary(false);
    }
  };

  // Fetch monthly summary when expense month/year changes
  useEffect(() => {
    fetchResidentMonthlySummary(expenseMonth, expenseYear);
  }, [expenseMonth, expenseYear]);

  const changeReportMonth = (delta) => {
    let newMonth = reportMonth + delta;
    let newYear = reportYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setReportMonth(newMonth);
    setReportYear(newYear);
  };

  // Fetch aidat income and monthly summary when report modal opens or month changes
  useEffect(() => {
    if (showDetailedReport) {
      fetchReportAidatIncome(reportMonth, reportYear);
      // Also fetch monthly summary for the detailed report (same endpoint as Admin)
      (async () => {
        setLoadingResidentReportSummary(true);
        try {
          const response = await axios.get(
            `${API}/admin/monthly-summary?month=${reportMonth + 1}&year=${reportYear}`,
            { withCredentials: true }
          );
          setResidentReportSummary(response.data);
        } catch (error) {
          console.error('Error fetching report summary:', error);
          setResidentReportSummary(null);
        } finally {
          setLoadingResidentReportSummary(false);
        }
      })();
    }
  }, [showDetailedReport, reportMonth, reportYear]);

  const fetchReportAidatIncome = async (month, year) => {
    try {
      setLoadingAidatIncome(true);
      // Note: API expects 1-based month (1-12), but JS uses 0-based (0-11)
      const apiMonth = month + 1;
      const url = `${API}/admin/aidat-income?month=${apiMonth}&year=${year}`;
      const response = await axios.get(url, { withCredentials: true });
      // Use total_aidat_income only (excludes other payments like kapıcı) - matches Operating Ledger
      setReportAidatIncome(response.data.total_aidat_income || 0);
    } catch (error) {
      console.error('Error fetching aidat income:', error);
      setReportAidatIncome(0);
    } finally {
      setLoadingAidatIncome(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Client-side validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError(t('passwordsDoNotMatch'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('passwordsDoNotMatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('passwordTooWeak'));
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, { withCredentials: true });

      setPasswordSuccess(true);
      toast.success(t('passwordChangedSuccess'));
      
      // Reset form and close modal after 2 seconds
      setTimeout(() => {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowChangePassword(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      if (error.response?.data?.detail === 'Current password is incorrect') {
        setPasswordError(t('currentPasswordIncorrect'));
      } else if (error.response?.data?.detail === 'New password must be at least 6 characters') {
        setPasswordError(t('passwordTooWeak'));
      } else if (error.response?.data?.detail === 'New password must be different from current password') {
        setPasswordError(t('newPasswordSameAsOld'));
      } else {
        setPasswordError(t('passwordChangeError'));
      }
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  // Calculate previous year balance
  const currentYear = new Date().getFullYear();
  const previousYearFees = userFees.filter(f => f.year < currentYear);
  const previousYearBalance = previousYearFees.reduce((sum, fee) => {
    return sum + (fee.status === 'unpaid' || fee.status === 'partially_paid' ? fee.amount : 0);
  }, 0);

  // Calculate overall statistics using cumulative data if available
  // Derive the active unit's owner/tenant context (supports multi-unit owners).
  const activeAptId = selectedAptId || user.apartment_id;
  const aptContext = aptContextList.find((a) => a.apartment_id === activeAptId) || null;
  const hasMultipleUnits = aptContextList.length > 1;
  // Owner-with-tenant is only responsible for their own (other-payment) debt; use the
  // backend per-unit own_debt so the tenant's dues are never shown as the owner's balance.
  const ownerWithTenant = aptContext && aptContext.role === 'owner' && aptContext.has_tenant;
  const totalUnpaidBalance = ownerWithTenant
    ? (aptContext?.own_debt || 0)
    : cumulativeData
    ? (() => {
        const lastRow = cumulativeData.filter(r => !r.is_devir).slice(-1)[0];
        return lastRow ? lastRow.cumulative_balance : 0;
      })()
    : userFees.reduce((sum, fee) => sum + (fee.status === 'unpaid' ? fee.amount : 0), 0);
  
  // Count total unpaid months (include partially paid)
  const totalUnpaidMonths = userFees.filter(fee => fee.status === 'unpaid' || fee.status === 'partially_paid').length;
  
  // Get last fee amount (most recent fee regardless of status)
  const sortedFees = [...userFees].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.month - a.month;
  });
  const lastFeeAmount = sortedFees.length > 0 ? sortedFees[0].amount : 0;
  
  // Generate last 6 months timeline data
  const generateLast6Months = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      
      const monthFees = userFees.filter(f => f.month === month && f.year === year);
      const hasUnpaid = monthFees.some(f => f.status === 'unpaid');
      const hasPartial = monthFees.some(f => f.status === 'partially_paid');
      
      months.push({
        month: month,
        year: year,
        monthIndex: date.getMonth(),
        status: hasUnpaid ? 'unpaid' : hasPartial ? 'partial' : 'paid',
        label: t(`monthShort.${month}`)
      });
    }
    
    return months;
  };
  
  const last6Months = generateLast6Months();

  // Build cumulative data grouped by year for the payment modal
  // Using Admin's formula: Kalan = Prev + Ödenen(payments_in_month) - Borç(aidat+diğer)
  // Admin convention: negative = debt, positive = credit
  const cumulByYear = {};
  const devirByYear = {};
  if (cumulativeData) {
    // Group raw data by year first
    const rawByYear = {};
    for (const row of cumulativeData) {
      const yr = row.year;
      if (row.type === 'opening_balance' || row.status === 'devir' || row.is_devir) {
        // Devir: negate to match Admin sign (backend positive=debt → display negative=debt)
        devirByYear[yr] = { ...row, adminCumulative: -(row.cumulative_balance || 0) };
      } else {
        if (row.payment_status === 'future') continue;
        if (!rawByYear[yr]) rawByYear[yr] = [];
        rawByYear[yr].push(row);
      }
    }

    // Second pass: calculate Admin-matching cumulative per year
    for (const yr of Object.keys(rawByYear)) {
      const devir = devirByYear[yr];
      let runningCumulative = devir ? devir.adminCumulative : 0;
      
      const enrichedRows = rawByYear[yr].map(row => {
        const aidatDebt = row.amount || 0;
        const otherPaymentDebt = row.other_payment_debt || 0;
        const totalMonthlyDebt = aidatDebt + otherPaymentDebt;
        const paymentsInMonth = row.payments_in_month || 0;
        
        runningCumulative = runningCumulative + paymentsInMonth - totalMonthlyDebt;
        
        return {
          ...row,
          adminPaidInMonth: paymentsInMonth,
          adminCumulative: runningCumulative
        };
      });
      
      cumulByYear[yr] = enrichedRows;
    }
  }
  const cumulSortedYears = Object.keys(cumulByYear).sort((a, b) => b - a);

  // Year-to-Date summary (January → current month), reusing the EXACT same source
  // (cumulByYear/devirByYear) that the Resident "Aidat Ödeme Durumu" screen uses.
  const ytdYear = currentYear;
  const ytdCurrentMonth = new Date().getMonth() + 1;
  const ytdDevir = devirByYear[ytdYear];
  const ytdRows = (cumulByYear[ytdYear] || []).filter(r => r.month <= ytdCurrentMonth);
  const ytdAidat = ytdRows.reduce((s, r) => s + (r.amount || 0), 0);
  const ytdOther = ytdRows.reduce((s, r) => s + (r.other_payment_debt || 0), 0);
  const ytdPaid = ytdRows.reduce((s, r) => s + (r.adminPaidInMonth || 0), 0);
  const ytdBalance = ytdRows.length > 0
    ? ytdRows[ytdRows.length - 1].adminCumulative
    : (ytdDevir ? ytdDevir.adminCumulative : 0);
  const ytdTitle = ytdCurrentMonth === 1
    ? `${getMonthName(0)} ${ytdYear}`
    : `${getMonthName(0)} – ${getMonthName(ytdCurrentMonth - 1)} ${ytdYear}`;

  // Calculate total remaining debt from cumulative data (Admin convention: negative=debt)
  const totalRemainingDebt = (() => {
    // Owner of a rented unit only owes their own (other-payment) charges, never the tenant's dues
    if (ownerWithTenant) return aptContext?.own_debt || 0;
    if (!cumulativeData || cumulativeData.length === 0) return 0;
    const latestYear = cumulSortedYears.length > 0 ? cumulSortedYears[0] : null;
    if (!latestYear || !cumulByYear[latestYear] || cumulByYear[latestYear].length === 0) return 0;
    const lastRow = cumulByYear[latestYear][cumulByYear[latestYear].length - 1];
    // Admin cumulative is negative when in debt, so negate to get positive debt amount
    return Math.max(0, -(lastRow?.adminCumulative || 0));
  })();

  // Set default payment amount when total debt changes or modal opens
  useEffect(() => {
    if (showPaymentStatus && totalRemainingDebt > 0) {
      setPaymentAmount(totalRemainingDebt.toFixed(2));
      setIsPaymentFocused(false);
    }
  }, [showPaymentStatus, totalRemainingDebt]);

  // Helper function to filter paid fees by payment date (not period)
  // This ensures Monthly Financial Summary matches Operating Ledger behavior
  const filterFeesByPaymentDate = (feesList, targetMonth, targetYear) => {
    return feesList.filter(f => {
      if (f.status !== 'paid' || !f.paid_date) return false;
      try {
        const paidDate = new Date(f.paid_date);
        if (isNaN(paidDate.getTime())) return false;
        return paidDate.getMonth() === targetMonth && paidDate.getFullYear() === targetYear;
      } catch {
        return false;
      }
    });
  };

  // Calculate income and expenses for the selected expense month/year on the card
  // IMPORTANT: Maintenance fee income is based on PAYMENT DATE, not fee period
  const expenseMonthFees = filterFeesByPaymentDate(fees, expenseMonth, expenseYear);
  const expenseMonthMaintenanceIncome = expenseMonthFees.reduce((sum, fee) => sum + fee.amount, 0);
  
  const expenseMonthOtherIncome = otherIncome.filter(income => {
    const incomeDate = new Date(income.date);
    return incomeDate.getMonth() === expenseMonth && incomeDate.getFullYear() === expenseYear;
  });
  const expenseMonthOtherIncomeTotal = expenseMonthOtherIncome.reduce((sum, income) => sum + income.amount, 0);
  
  const expenseMonthExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.date);
    return expenseDate.getMonth() === expenseMonth && expenseDate.getFullYear() === expenseYear;
  });
  const expenseMonthExpensesTotal = expenseMonthExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  
  const expenseMonthInvestments = investments.filter(investment => {
    if (!investment.start_date) return false;
    const startDate = new Date(investment.start_date);
    return startDate.getMonth() === expenseMonth && startDate.getFullYear() === expenseYear;
  });
  const expenseMonthInvestmentsTotal = expenseMonthInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  
  const totalIncome = expenseMonthMaintenanceIncome + expenseMonthOtherIncomeTotal;
  const totalExpense = expenseMonthExpensesTotal + expenseMonthInvestmentsTotal;
  const netBalance = totalIncome - totalExpense;

  // Calculate ending balance for a specific month (recursive)
  // Balance(M) = Carryover(M) + Income(M) - Expense(M)
  // Carryover(M) = Balance(M-1)
  // IMPORTANT: Maintenance fee income is based on PAYMENT DATE, not fee period
  const calculateMonthBalance = (targetMonth, targetYear, visitedMonths = new Set()) => {
    const monthKey = `${targetYear}-${targetMonth}`;
    
    // Prevent infinite recursion
    if (visitedMonths.has(monthKey)) {
      return 0;
    }
    visitedMonths.add(monthKey);
    
    // Get this month's maintenance fees income based on PAYMENT DATE
    const monthMaintenanceFees = filterFeesByPaymentDate(fees, targetMonth, targetYear);
    const monthMaintenanceIncome = monthMaintenanceFees.reduce((sum, fee) => sum + fee.amount, 0);
    
    // Get this month's other income
    const monthOtherIncome = otherIncome.filter(income => {
      const incomeDate = new Date(income.date);
      return incomeDate.getMonth() === targetMonth && incomeDate.getFullYear() === targetYear;
    });
    const monthOtherIncomeTotal = monthOtherIncome.reduce((sum, income) => sum + income.amount, 0);
    
    // Get this month's expenses
    const monthExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getMonth() === targetMonth && expenseDate.getFullYear() === targetYear;
    });
    const monthExpensesTotal = monthExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    
    // Get this month's investments
    const monthInvestments = investments.filter(investment => {
      if (!investment.start_date) return false;
      const startDate = new Date(investment.start_date);
      return startDate.getMonth() === targetMonth && startDate.getFullYear() === targetYear;
    });
    const monthInvestmentsTotal = monthInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    
    const monthIncome = monthMaintenanceIncome + monthOtherIncomeTotal;
    const monthExpense = monthExpensesTotal + monthInvestmentsTotal;
    
    // Find the earliest data point to determine base month
    // Use payment dates for fees, not period
    const allDates = [
      ...fees.filter(f => f.status === 'paid' && f.paid_date).map(f => new Date(f.paid_date)),
      ...otherIncome.map(i => new Date(i.date)),
      ...expenses.map(e => new Date(e.date)),
      ...investments.filter(i => i.start_date).map(i => new Date(i.start_date))
    ].filter(d => !isNaN(d.getTime()));
    
    const earliestDate = allDates.length > 0 
      ? new Date(Math.min(...allDates.map(d => d.getTime())))
      : new Date();
    const earliestMonth = earliestDate.getMonth();
    const earliestYear = earliestDate.getFullYear();
    
    // If this is the earliest month or before, carryover is 0
    if (targetYear < earliestYear || (targetYear === earliestYear && targetMonth <= earliestMonth)) {
      return monthIncome - monthExpense;
    }
    
    // Calculate previous month
    const prevMonth = targetMonth === 0 ? 11 : targetMonth - 1;
    const prevYear = targetMonth === 0 ? targetYear - 1 : targetYear;
    
    // Recursively get previous month's ending balance (which becomes this month's carryover)
    const carryoverFromPrev = calculateMonthBalance(prevMonth, prevYear, visitedMonths);
    
    return carryoverFromPrev + monthIncome - monthExpense;
  };
  
  // Calculate carryover for the current display month
  // Carryover(M) = Ending Balance of month (M-1)
  const calculateCarryover = (month, year) => {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    
    // Find the earliest data point - use payment dates for fees
    const allDates = [
      ...fees.filter(f => f.status === 'paid' && f.paid_date).map(f => new Date(f.paid_date)),
      ...otherIncome.map(i => new Date(i.date)),
      ...expenses.map(e => new Date(e.date)),
      ...investments.filter(i => i.start_date).map(i => new Date(i.start_date))
    ].filter(d => !isNaN(d.getTime()));
    
    if (allDates.length === 0) {
      return 0; // No data, no carryover
    }
    
    const earliestDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const earliestMonth = earliestDate.getMonth();
    const earliestYear = earliestDate.getFullYear();
    
    // If we're viewing the earliest month or before, carryover is 0
    if (year < earliestYear || (year === earliestYear && month <= earliestMonth)) {
      return 0;
    }
    
    // Get the ending balance of the previous month (recursively calculated)
    return calculateMonthBalance(prevMonth, prevYear);
  };
  
  const carryover = calculateCarryover(expenseMonth, expenseYear);
  const currentBalance = carryover + totalIncome - totalExpense;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Dark Background */}
      <header className="bg-gray-800 text-white sticky top-0 z-50 shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('residentPortal')}</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm hidden sm:inline" style={{ fontFamily: 'Inter, sans-serif' }}>{user.name}</span>
              <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center font-bold text-sm">
                {getInitials(user.name)}
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="px-3 py-1 text-sm hover:bg-gray-700 rounded transition-colors"
                title={t('settings')}
              >
                ⚙️
              </button>
              {user.auth_type === 'manual' && (
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="px-3 py-1 text-sm hover:bg-gray-700 rounded transition-colors"
                  title={t('changePassword')}
                >
                  🔑
                </button>
              )}
              <button
                onClick={logout}
                className="px-3 py-1 text-sm hover:bg-gray-700 rounded transition-colors"
              >
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile-First Single Column */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-2xl">
        
        {/* SECTION 1: MAINTENANCE FEE STATUS - LAST 6 MONTHS */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          {/* Header */}
          <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4 sm:mb-6 flex flex-wrap items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span data-testid="resident-ytd-title">{ytdTitle}</span>
            {aptContext && (
              <span
                data-testid="resident-role-badge"
                className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${aptContext.role === 'tenant' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}
              >
                {aptContext.role === 'tenant' ? (t('tenantLabel') || 'Kiracı') : (t('ownerLabel') || 'Malik')}
              </span>
            )}
            {aptContext && aptContext.role === 'owner' && aptContext.has_tenant && (
              <span data-testid="resident-tenant-readonly-note" className="inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
                {t('tenantDebtReadOnly') || "Tenant's debt (read-only)"}
              </span>
            )}
          </h2>

          {/* Multi-unit selector (owner of several apartments) */}
          {hasMultipleUnits && (
            <div className="mb-4" data-testid="resident-unit-selector-wrap">
              <label className="block text-xs font-semibold text-gray-600 mb-1">{t('selectUnit') || 'Daire seçin'}</label>
              <select
                data-testid="resident-unit-selector"
                value={activeAptId || ''}
                onChange={(e) => setSelectedAptId(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700"
              >
                {aptContextList.map((a) => (
                  <option key={a.apartment_id} value={a.apartment_id}>
                    {`${t('unitNumber')} ${a.unit_number}`}{a.tenant_name ? ` — ${t('tenantLabel')}: ${a.tenant_name}` : ''}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-500 mt-2" data-testid="resident-total-all-units">
                {(t('totalAllUnits') || 'Toplam borç (tüm daireler)')}: <span className="font-bold text-red-600">{formatCurrency(aptContextTotal)}</span>
              </div>
            </div>
          )}

          {/* Tenant read-only dues info for the owner of a rented unit */}
          {ownerWithTenant && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="owner-tenant-debt-info">
              <span className="font-semibold">{aptContext.tenant_name}</span> — {(t('tenantDuesReadOnly') || 'Kiracı aidat borcu (salt okunur)')}: <span className="font-bold">{formatCurrency(aptContext.tenant_debt || 0)}</span>
            </div>
          )}

          {/* Year-to-Date Financial Summary (January → current month) */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6" data-testid="resident-ytd-summary">
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 mb-1">{t('maintenanceFee')}</div>
              <div className="text-base sm:text-lg font-bold text-gray-800 break-all" data-testid="ytd-aidat">{formatCurrency(ytdAidat)}</div>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 mb-1">{t('otherPaymentCol')}</div>
              <div className="text-base sm:text-lg font-bold text-purple-700 break-all" data-testid="ytd-other">{ytdOther > 0 ? formatCurrency(ytdOther) : '-'}</div>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 mb-1">{t('paidAmountCol')}</div>
              <div className="text-base sm:text-lg font-bold text-green-600 break-all" data-testid="ytd-paid">{formatCurrency(ytdPaid)}</div>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 mb-1">{t('balance')}</div>
              <div className={`text-base sm:text-lg font-bold break-all ${ytdBalance < 0 ? 'text-red-600' : ytdBalance > 0 ? 'text-green-600' : 'text-gray-600'}`} data-testid="ytd-balance">
                {ytdBalance > 0 ? `+${formatCurrency(ytdBalance)}` : formatCurrency(ytdBalance)}
              </div>
            </div>
          </div>

          {/* View Report and Pay Button */}
          <button 
            onClick={() => setShowPaymentStatus(true)}
            className="w-full bg-teal-600 text-white py-2.5 sm:py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors text-sm sm:text-base"
          >
            {t('viewReportAndPay')}
          </button>
        </div>

        {/* SECTION 2: LATEST ANNOUNCEMENTS (SON DUYURULAR) */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('recentAnnouncements')}</h2>
          
          <div className="space-y-3 sm:space-y-4">
            {stats?.latest_announcements && stats.latest_announcements.length > 0 ? (
              stats.latest_announcements.map((announcement) => (
                <div key={announcement.id} className="border-l-4 border-teal-500 pl-3 sm:pl-4 py-2">
                  <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
                  <div className="text-xs text-gray-500 mt-1">
                    {new Date(announcement.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{announcement.content}</p>
                </div>
              ))
            ) : (
              <>
                <div className="border-l-4 border-teal-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900">Tadilat</h3>
                  <div className="text-xs text-gray-500 mt-1">25 Ekim 2023</div>
                  <p className="text-sm text-gray-600 mt-2">Bina girişinde tadilat çalışmaları devam etmektedir. Lütfen dikkatli olunuz.</p>
                </div>
                <div className="border-l-4 border-teal-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900">Apartman Toplantısı</h3>
                  <div className="text-xs text-gray-500 mt-1">20 Ekim 2023</div>
                  <p className="text-sm text-gray-600 mt-2">Aylık apartman toplantısı 15 Kasım Çarşamba günü saat 19:00'da yapılacaktır.</p>
                </div>
                <div className="border-l-4 border-teal-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900">Aidat Ödemeleri</h3>
                  <div className="text-xs text-gray-500 mt-1">15 Ekim 2023</div>
                  <p className="text-sm text-gray-600 mt-2">Ekim ayı aidat ödemelerinin 30 Ekim'e kadar yapılması gerekmektedir.</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* SECTION 3: MONTHLY FINANCIAL SUMMARY */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6" data-testid="resident-monthly-summary">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('monthlySummary')}</h2>
          </div>

          {/* Month Selector - Admin-style with clickable picker */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <button 
              onClick={() => changeExpenseMonth(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              data-testid="resident-prev-month"
            >
              ←
            </button>
            <button 
              onClick={() => {
                setResSummaryPickerMonth(expenseMonth);
                setResSummaryPickerYear(expenseYear);
                setShowResidentSummaryPicker(true);
              }}
              className="text-center font-semibold text-gray-700 text-sm sm:text-base min-w-[120px] hover:bg-gray-100 px-3 py-1 rounded-full transition-all cursor-pointer"
              data-testid="resident-current-month"
            >
              {getMonthName(expenseMonth)} {expenseYear}
            </button>
            <button 
              onClick={() => changeExpenseMonth(1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              data-testid="resident-next-month"
            >
              →
            </button>
          </div>

          {/* 2x2 Grid for 4 Metrics - Using backend data */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
            {/* Carryover - Secondary */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-2 sm:p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('carryover')}</div>
              </div>
              <div className="text-xs text-gray-400 mb-1 hidden sm:block">{t('carryoverFromPrevMonth')}</div>
              <div className="text-sm sm:text-lg font-semibold text-gray-600 break-all" data-testid="resident-carryover">
                {loadingResidentSummary ? '...' : (residentMonthlySummary?.carryover >= 0 ? '+' : '')}{loadingResidentSummary ? '' : formatCurrency(residentMonthlySummary?.carryover || 0)}
              </div>
            </div>

            {/* Income - Secondary */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-2 sm:p-3 border border-green-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-green-600 uppercase tracking-wide">{t('income')}</div>
                <div className="text-xs text-green-500">+</div>
              </div>
              <div className="text-sm sm:text-lg font-semibold text-green-600 break-all" data-testid="resident-income">
                {loadingResidentSummary ? '...' : formatCurrency(residentMonthlySummary?.total_income || 0)}
              </div>
            </div>

            {/* Expenses - Secondary */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-2 sm:p-3 border border-red-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-red-600 uppercase tracking-wide">{t('expenses')}</div>
                <div className="text-xs text-red-500">−</div>
              </div>
              <div className="text-sm sm:text-lg font-semibold text-red-600 break-all" data-testid="resident-expenses">
                {loadingResidentSummary ? '...' : formatCurrency(residentMonthlySummary?.total_expenses || 0)}
              </div>
            </div>

            {/* Balance - PRIMARY (Most Prominent) */}
            <div className={`bg-gradient-to-br rounded-xl p-2 sm:p-3 border-2 shadow-md ${
              (residentMonthlySummary?.balance || 0) >= 0 
                ? 'from-blue-100 to-blue-200 border-blue-400' 
                : 'from-red-100 to-red-200 border-red-400'
            }`}>
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs font-bold uppercase tracking-wide ${
                  (residentMonthlySummary?.balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'
                }`}>
                  {t('balance')}
                </div>
                <div className={`text-xs font-bold ${(residentMonthlySummary?.balance || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  =
                </div>
              </div>
              <div className={`text-lg sm:text-2xl font-bold break-all ${(residentMonthlySummary?.balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="resident-balance">
                {loadingResidentSummary ? '...' : ((residentMonthlySummary?.balance || 0) >= 0 ? '+' : '')}{loadingResidentSummary ? '' : formatCurrency(residentMonthlySummary?.balance || 0)}
              </div>
            </div>
          </div>

          {/* View Detailed Report Button */}
          <button 
            onClick={() => {
              setReportMonth(expenseMonth);
              setReportYear(expenseYear);
              setShowDetailedReport(true);
            }}
            className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors shadow-sm"
            data-testid="view-detailed-report-btn"
          >
            {t('viewDetailedReport')}
          </button>
        </div>

        {/* SECTION 4: INVESTMENTS / PROJECTS (YATIRIMLAR) */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('investments')}</h2>
          
          {investments.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {investments.map((investment) => {
                const getStatusColor = (status) => {
                  if (status === 'completed') return 'text-green-600';
                  if (status === 'in_progress') return 'text-orange-600';
                  return 'text-blue-600';
                };
                
                const getBorderColor = (status) => {
                  if (status === 'completed') return 'border-green-500';
                  if (status === 'in_progress') return 'border-orange-500';
                  return 'border-blue-500';
                };
                
                const getStatusLabel = (status) => {
                  if (status === 'completed') return t('completed');
                  if (status === 'in_progress') return t('inProgress');
                  return t('planned');
                };
                
                return (
                  <div key={investment.id} className={`border-l-4 ${getBorderColor(investment.status)} pl-3 sm:pl-4 py-2`}>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{investment.title}</h3>
                    <div className={`text-xs font-semibold mt-1 ${getStatusColor(investment.status)}`}>
                      {getStatusLabel(investment.status)} {investment.completed_date ? `- ${investment.completed_date}` : investment.start_date ? `- ${investment.start_date}` : ''}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">{investment.description}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <p className="text-sm">{t('noInvestmentsYet')}</p>
            </div>
          )}
        </div>

      </div>

      {/* Month/Year Picker Modal - Resident Monthly Summary */}
      {showResidentSummaryPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="resident-summary-month-picker-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('selectMonthYear') || 'Select Month/Year'}
            </h3>
            
            {/* Year Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('year') || 'Year'}</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setResSummaryPickerYear(prev => prev - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  ←
                </button>
                <span className="flex-1 text-center text-xl font-bold text-gray-800">{resSummaryPickerYear}</span>
                <button
                  onClick={() => setResSummaryPickerYear(prev => prev + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  →
                </button>
              </div>
            </div>
            
            {/* Month Selection Grid */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('month') || 'Month'}</label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((month) => (
                  <button
                    key={month}
                    onClick={() => setResSummaryPickerMonth(month)}
                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-colors ${
                      resSummaryPickerMonth === month
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`resident-summary-picker-${month}`}
                  >
                    {getMonthName(month).substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Selected Preview */}
            <div className="mb-4 p-3 bg-teal-50 rounded-lg text-center">
              <span className="text-teal-700 font-semibold">
                {getMonthName(resSummaryPickerMonth)} {resSummaryPickerYear}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowResidentSummaryPicker(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                data-testid="resident-summary-picker-cancel"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  setExpenseMonth(resSummaryPickerMonth);
                  setExpenseYear(resSummaryPickerYear);
                  setShowResidentSummaryPicker(false);
                }}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                data-testid="resident-summary-picker-apply"
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Month/Year Picker Modal - Resident Report Detail */}
      {showResidentReportPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="resident-report-month-picker-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('selectMonthYear') || 'Select Month/Year'}
            </h3>
            
            {/* Year Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('year') || 'Year'}</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setResReportPickerYear(prev => prev - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  ←
                </button>
                <span className="flex-1 text-center text-xl font-bold text-gray-800">{resReportPickerYear}</span>
                <button
                  onClick={() => setResReportPickerYear(prev => prev + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  →
                </button>
              </div>
            </div>
            
            {/* Month Selection Grid */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('month') || 'Month'}</label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((month) => (
                  <button
                    key={month}
                    onClick={() => setResReportPickerMonth(month)}
                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-colors ${
                      resReportPickerMonth === month
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`resident-report-picker-${month}`}
                  >
                    {getMonthName(month).substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Selected Preview */}
            <div className="mb-4 p-3 bg-teal-50 rounded-lg text-center">
              <span className="text-teal-700 font-semibold">
                {getMonthName(resReportPickerMonth)} {resReportPickerYear}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowResidentReportPicker(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                data-testid="resident-report-picker-cancel"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  setReportMonth(resReportPickerMonth);
                  setReportYear(resReportPickerYear);
                  setShowResidentReportPicker(false);
                }}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                data-testid="resident-report-picker-apply"
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AIDAT ÖDEME DURUMU MODAL */}
      {showPaymentStatus && (() => {
        // Validate payment amount
        const parsedPaymentAmount = parseFloat(paymentAmount) || 0;
        const isPaymentValid = parsedPaymentAmount > 0 && parsedPaymentAmount <= totalRemainingDebt;

        // Format raw paymentAmount (dot-decimal) for locale display
        const formatPaymentDisplay = (raw) => {
          const num = parseFloat(raw);
          if (isNaN(num)) return raw || '';
          if (language === 'en') {
            return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
          return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        // Show locale-appropriate raw value on focus, formatted on blur
        const paymentInputValue = isPaymentFocused
          ? (language !== 'en' ? paymentAmount.replace('.', ',') : paymentAmount)
          : formatPaymentDisplay(paymentAmount);

        // onChange: normalize to dot-decimal, limit 2 decimal places
        const handlePaymentAmountChange = (e) => {
          let val = e.target.value;
          // Allow only digits, dot, comma
          val = val.replace(/[^0-9.,]/g, '');
          // Normalize comma to dot for internal storage
          val = val.replace(',', '.');
          // Ensure only one decimal separator
          const dotIdx = val.indexOf('.');
          if (dotIdx !== -1) {
            val = val.substring(0, dotIdx + 1) + val.substring(dotIdx + 1).replace(/\./g, '');
          }
          // Limit to 2 decimal digits
          const parts = val.split('.');
          if (parts.length === 2 && parts[1].length > 2) {
            val = parts[0] + '.' + parts[1].substring(0, 2);
          }
          setPaymentAmount(val);
        };

        // onBlur: round to 2 decimals for clean storage
        const handlePaymentBlur = () => {
          setIsPaymentFocused(false);
          const num = parseFloat(paymentAmount);
          if (!isNaN(num) && num > 0) {
            setPaymentAmount(num.toFixed(2));
          }
        };

        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto my-4 sm:my-8" data-testid="payment-status-modal">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('paymentStatusTitle')}
              </h2>
              <button 
                onClick={() => setShowPaymentStatus(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 text-xl"
                data-testid="close-payment-modal"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6">
              {/* YEARLY SECTIONS from cumulative data */}
              {cumulSortedYears.length > 0 ? (
                <div className="space-y-4">
                  {cumulSortedYears.map(year => {
                    const isExpanded = expandedYears[year] !== false;
                    const yearRows = cumulByYear[year] || [];
                    const yearDevir = devirByYear[year];
                    const yearUnpaid = yearRows.filter(r => r.payment_status === 'unpaid' || r.payment_status === 'partially_paid').length;
                    const lastRowInYear = yearRows.length > 0 ? yearRows[yearRows.length - 1] : null;
                    // Use Admin-matching cumulative (negative=debt)
                    const yearEndBalance = lastRowInYear?.adminCumulative || (yearDevir?.adminCumulative || 0);

                    // Calculate year totals using Admin-matching values
                    const yearTotalAidat = yearRows.reduce((s, r) => s + (r.amount || 0), 0);
                    const yearTotalOther = yearRows.reduce((s, r) => s + (r.other_payment_debt || 0), 0);
                    const yearTotalPaid = yearRows.reduce((s, r) => s + (r.adminPaidInMonth || 0), 0);

                    return (
                      <div key={year} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`year-section-${year}`}>
                        {/* Year Header - clickable accordion */}
                        <button
                          data-testid={`year-toggle-${year}`}
                          onClick={() => setExpandedYears(prev => ({ ...prev, [year]: !isExpanded }))}
                          className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <h3 className="font-bold text-gray-800">{year} {t('year')}</h3>
                            {yearUnpaid > 0 && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                {yearUnpaid} {t('unpaid').toLowerCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {yearEndBalance < 0 && (
                              <span className="text-xs text-red-600 font-medium" data-testid={`year-balance-${year}`}>{formatCurrency(yearEndBalance)}</span>
                            )}
                            <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                        
                        {isExpanded && (
                          <>
                            {/* Opening Balance (Devir) at top of year section */}
                            {yearDevir && yearDevir.adminCumulative !== 0 && (
                              <div className={`mx-3 sm:mx-4 mt-3 rounded-lg p-3 ${yearDevir.adminCumulative < 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid={`devir-row-${year}`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-semibold text-gray-800 text-sm">{t('devir')}</span>
                                    <span className="text-xs text-gray-500 ml-2">{t('devirDesc')}</span>
                                  </div>
                                  <span className={`text-lg font-bold ${yearDevir.adminCumulative < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {formatCurrency(yearDevir.adminCumulative)}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Mobile Card Layout */}
                            <div className="block sm:hidden divide-y divide-gray-100 mt-2">
                              {yearRows.map(row => {
                                const otherPayments = row.other_payments || [];
                                const isPaid = row.payment_status === 'paid';
                                const isPartial = row.payment_status === 'partially_paid';
                                const paidInMonth = row.adminPaidInMonth || 0;
                                const cumulBal = row.adminCumulative;

                                return (
                                  <div key={row.id} className="p-3 hover:bg-gray-50" data-testid={`fee-row-mobile-${row.year}-${row.month}`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-semibold text-gray-800 text-sm">{getMonthName(row.month - 1)}</span>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">{t('maintenanceFee')}</span>
                                        <span className="font-medium text-gray-800">{formatCurrency(row.amount)}</span>
                                      </div>
                                      {(row.other_payment_debt || 0) > 0 && (
                                        <div className="flex justify-between">
                                          <span className="text-purple-600">
                                            {otherPayments.length > 0 ? otherPayments[0].description || t('otherPaymentCol') : t('otherPaymentCol')}
                                          </span>
                                          <span className="font-medium text-purple-700">{formatCurrency(row.other_payment_debt)}</span>
                                        </div>
                                      )}
                                      {paidInMonth > 0 && (
                                        <div className="flex justify-between">
                                          <span className="text-green-600">{t('paidAmountCol')}</span>
                                          <span className="font-medium text-green-700">{formatCurrency(paidInMonth)}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between pt-1 border-t border-gray-100">
                                        <span className="text-gray-500 text-xs">{t('remainingCumulative')}</span>
                                        <span className={`font-bold text-xs ${cumulBal < 0 ? 'text-red-600' : cumulBal > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                          {formatCurrency(cumulBal)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Desktop Table Layout */}
                            <div className="hidden sm:block overflow-x-auto mt-2">
                              <table className="w-full" data-testid={`fee-table-${year}`}>
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">{t('period')}</th>
                                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t('maintenanceFee')}</th>
                                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t('otherPaymentCol')}</th>
                                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t('paidAmountCol')}</th>
                                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t('remainingCumulative')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {yearRows.map(row => {
                                    const otherPayments = row.other_payments || [];
                                    const isPaid = row.payment_status === 'paid';
                                    const isPartial = row.payment_status === 'partially_paid';
                                    const paidInMonth = row.adminPaidInMonth || 0;
                                    const cumulBal = row.adminCumulative;

                                    return (
                                      <tr key={row.id} className="hover:bg-gray-50 align-top" data-testid={`fee-row-${row.year}-${row.month}`}>
                                        <td className="px-3 py-2.5 text-sm">
                                          <div className="flex items-center gap-2">
                                            <span className="text-gray-800 font-medium">{getMonthName(row.month - 1)}</span>
                                          </div>
                                        </td>
                                        <td className="px-3 py-2.5 text-sm text-right font-medium text-gray-800">
                                          {formatCurrency(row.amount)}
                                        </td>
                                        <td className="px-3 py-2.5 text-sm text-right">
                                          {(row.other_payment_debt || 0) > 0 ? (
                                            <div>
                                              <span className="font-medium text-purple-700">{formatCurrency(row.other_payment_debt)}</span>
                                              {otherPayments.length > 0 && otherPayments[0].description && (
                                                <div className="text-[10px] text-purple-500 mt-0.5">{otherPayments[0].description}</div>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-gray-300">-</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2.5 text-sm text-right">
                                          {paidInMonth > 0 ? (
                                            <span className="font-medium text-green-700">{formatCurrency(paidInMonth)}</span>
                                          ) : (
                                            <span className="text-gray-300">-</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2.5 text-sm text-right">
                                          <span className={`font-bold ${cumulBal < 0 ? 'text-red-600' : cumulBal > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                            {formatCurrency(cumulBal)}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                {/* Year Totals Footer */}
                                <tfoot>
                                  <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold text-sm" data-testid={`year-totals-${year}`}>
                                    <td className="px-3 py-2.5 text-gray-700">{t('yearSummary')}</td>
                                    <td className="px-3 py-2.5 text-right text-gray-800">{formatCurrency(yearTotalAidat)}</td>
                                    <td className="px-3 py-2.5 text-right text-purple-700">{yearTotalOther > 0 ? formatCurrency(yearTotalOther) : '-'}</td>
                                    <td className="px-3 py-2.5 text-right text-green-700">{yearTotalPaid > 0 ? formatCurrency(yearTotalPaid) : '-'}</td>
                                    <td className="px-3 py-2.5 text-right">
                                      <span className={`font-bold ${yearEndBalance < 0 ? 'text-red-600' : yearEndBalance > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                        {formatCurrency(yearEndBalance)}
                                      </span>
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>

                            {/* Mobile Totals */}
                            <div className="block sm:hidden mx-3 mb-3 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid={`year-totals-mobile-${year}`}>
                              <div className="text-xs font-semibold text-gray-500 mb-2">{t('yearSummary')}</div>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">{t('totalAidat')}</span>
                                  <span className="font-medium">{formatCurrency(yearTotalAidat)}</span>
                                </div>
                                {yearTotalOther > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-purple-600">{t('totalOtherPayment')}</span>
                                    <span className="font-medium text-purple-700">{formatCurrency(yearTotalOther)}</span>
                                  </div>
                                )}
                                {yearTotalPaid > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-green-600">{t('totalPaid')}</span>
                                    <span className="font-medium text-green-700">{formatCurrency(yearTotalPaid)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between pt-1 border-t border-gray-200">
                                  <span className="font-semibold text-gray-700">{t('totalRemaining')}</span>
                                  <span className={`font-bold ${yearEndBalance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {formatCurrency(yearEndBalance)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('noFeesFound')}</p>
                </div>
              )}

              {/* PAYMENT SECTION - Total debt + editable amount + pay button */}
              {totalRemainingDebt > 0 && (
                <div className="mt-6 pt-5 border-t-2 border-gray-200" data-testid="payment-section">
                  {/* Total Debt Summary */}
                  <div className="flex items-center justify-between mb-4 px-1">
                    <span className="text-sm font-semibold text-gray-700">{t('totalDebt')}</span>
                    <span className="text-xl font-bold text-red-600" data-testid="total-debt-amount">{formatCurrency(totalRemainingDebt)}</span>
                  </div>

                  {/* Editable Payment Amount */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="payment-amount-input">
                      {t('paymentAmountLabel')}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₺</span>
                      <input
                        id="payment-amount-input"
                        data-testid="payment-amount-input"
                        type="text"
                        inputMode="decimal"
                        value={paymentInputValue}
                        onChange={handlePaymentAmountChange}
                        onFocus={() => setIsPaymentFocused(true)}
                        onBlur={handlePaymentBlur}
                        className={`w-full pl-8 pr-4 py-3 border rounded-xl text-right text-lg font-semibold focus:outline-none focus:ring-2 transition-colors ${
                          paymentAmount && !isPaymentValid
                            ? 'border-red-300 focus:ring-red-200 bg-red-50'
                            : 'border-gray-300 focus:ring-teal-200 focus:border-teal-400'
                        }`}
                      />
                    </div>
                    {paymentAmount && parsedPaymentAmount > totalRemainingDebt && (
                      <p className="text-xs text-red-500 mt-1">{t('paymentAmountExceedsDebt')}</p>
                    )}
                    {paymentAmount && parsedPaymentAmount <= 0 && paymentAmount !== '' && (
                      <p className="text-xs text-red-500 mt-1">{t('paymentAmountMustBePositive')}</p>
                    )}
                  </div>

                  {/* Pay Button */}
                  <button 
                    data-testid="make-payment-btn"
                    onClick={() => {
                      if (isPaymentValid) {
                        setShowPaymentSummary(true);
                      }
                    }}
                    disabled={!isPaymentValid}
                    className={`w-full py-3 rounded-xl font-semibold transition-colors text-base ${
                      !isPaymentValid
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {t('makePayment')} {isPaymentValid ? `(${formatCurrency(parsedPaymentAmount)})` : ''}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Payment Summary Modal */}
      {showPaymentSummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" data-testid="payment-summary-modal">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('paymentSummaryTitle')}
              </h2>
              <button 
                onClick={() => setShowPaymentSummary(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 text-xl"
                data-testid="close-summary-modal"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Debt & Payment Summary */}
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
                  <span className="text-sm text-gray-600">{t('totalDebt')}</span>
                  <span className="text-sm font-bold text-red-600">{formatCurrency(totalRemainingDebt)}</span>
                </div>
                <div className="bg-teal-50 px-4 py-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-800">{t('paymentAmountLabel')}</span>
                    <span className="text-xl font-bold text-teal-600" data-testid="summary-payment-amount">
                      {formatCurrency(parseFloat(paymentAmount) || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowPaymentSummary(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                  data-testid="cancel-payment-btn"
                >
                  {t('cancelPayment')}
                </button>
                <button 
                  data-testid="confirm-payment-btn"
                  onClick={() => {
                    setShowPaymentSummary(false);
                    setShowPaymentStatus(false);
                    toast.info(language === 'en' ? 'Payment gateway integration coming soon' : language === 'az' ? 'Ödəniş qapısı inteqrasiyası tezliklə' : 'Ödeme entegrasyonu yakında eklenecektir');
                  }}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors"
                >
                  {t('confirmPayment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Income/Expense Report Modal */}
      {showDetailedReport && (() => {
        // Use backend monthly summary for the 4-box totals (same as Admin)
        const reportCarryover = residentReportSummary?.carryover || 0;
        const reportTotalIncome = residentReportSummary?.total_income || 0;
        const reportTotalExpenses = residentReportSummary?.total_expenses || 0;
        const reportBalance = residentReportSummary?.balance || 0;
        const loadingReportSummary = loadingResidentReportSummary;
        const poolIncome = residentReportSummary?.pool_income || 0;
        const otherPaymentIncome = residentReportSummary?.other_payment_income || 0;

        // Aidat income is now fetched from the backend via the new endpoint
        // This properly matches Operating Ledger logic - income by PAYMENT DATE
        const maintenanceFeeIncome = reportAidatIncome;
        
        // Get itemized other income for the selected month
        const reportMonthOtherIncomeItems = otherIncome.filter(income => {
          const incomeDate = new Date(income.date);
          return incomeDate.getMonth() === reportMonth && incomeDate.getFullYear() === reportYear;
        });
        
        const totalOtherIncome = reportMonthOtherIncomeItems.reduce((sum, income) => sum + income.amount, 0);
        
        // Get itemized expenses for selected report month/year
        const reportMonthExpenseItems = expenses.filter(expense => {
          const expenseDate = new Date(expense.date);
          return expenseDate.getMonth() === reportMonth && expenseDate.getFullYear() === reportYear;
        });
        
        const totalRegularExpenses = reportMonthExpenseItems.reduce((sum, exp) => sum + exp.amount, 0);
        
        // Get itemized investments for the selected month
        const reportMonthInvestmentItems = investments.filter(investment => {
          if (!investment.start_date) return false;
          const startDate = new Date(investment.start_date);
          return startDate.getMonth() === reportMonth && startDate.getFullYear() === reportYear;
        });
        
        const totalInvestments = reportMonthInvestmentItems.reduce((sum, inv) => sum + (inv.amount || 0), 0);
        
        // Helper to translate other income category
        const translateOtherIncomeCategory = (category) => {
          const categoryMap = {
            commonAreaRental: t('commonAreaRental'),
            parkingFees: t('parkingFees'),
            facilityUsageFees: t('facilityUsageFees'),
            penalties: t('penalties'),
            keyCardFees: t('keyCardFees'),
            otherCategory: t('otherCategory')
          };
          return categoryMap[category] || category;
        };
        
        // Helper to translate expense category
        const translateExpenseCategory = (categoryKey) => {
          if (!categoryKey) return '';
          const translatedKey = t(`expenseCategories.${categoryKey}`);
          if (translatedKey === `expenseCategories.${categoryKey}`) {
            return categoryKey;
          }
          return translatedKey;
        };
        
        // Helper to translate expense subcategory
        const translateExpenseSubcategory = (categoryKey, subcategoryKey) => {
          if (!categoryKey || !subcategoryKey) return '';
          const translatedKey = t(`expenseSubcategories.${categoryKey}.${subcategoryKey}`);
          if (translatedKey === `expenseSubcategories.${categoryKey}.${subcategoryKey}`) {
            return subcategoryKey;
          }
          return translatedKey;
        };
        
        // Helper to translate investment category
        const translateInvestmentCategory = (categoryKey) => {
          if (!categoryKey) return '';
          const translatedKey = t(`investmentCategories.${categoryKey}`);
          if (translatedKey === `investmentCategories.${categoryKey}`) {
            return categoryKey;
          }
          return translatedKey;
        };
        
        // Helper to translate investment subcategory
        const translateInvestmentSubcategory = (categoryKey, subcategoryKey) => {
          if (!categoryKey || !subcategoryKey) return '';
          const translatedKey = t(`investmentSubcategories.${categoryKey}.${subcategoryKey}`);
          if (translatedKey === `investmentSubcategories.${categoryKey}.${subcategoryKey}`) {
            return subcategoryKey;
          }
          return translatedKey;
        };
        
        // Format date helper
        const formatDate = (dateStr) => {
          if (!dateStr) return '-';
          const date = new Date(dateStr);
          return date.toLocaleDateString();
        };
        
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto my-4 sm:my-8">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {t('detailedReportTitle')}
                </h2>
                <button 
                  onClick={() => setShowDetailedReport(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>

              {/* Month Selector - Admin-style with clickable picker */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <button 
                    onClick={() => changeReportMonth(-1)}
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => {
                      setResReportPickerMonth(reportMonth);
                      setResReportPickerYear(reportYear);
                      setShowResidentReportPicker(true);
                    }}
                    className="text-center font-bold text-gray-800 text-base sm:text-lg min-w-[140px] hover:bg-gray-200 px-3 py-1 rounded-full transition-all cursor-pointer"
                    data-testid="resident-report-month-picker-btn"
                  >
                    {getMonthName(reportMonth)} {reportYear}
                  </button>
                  <button 
                    onClick={() => changeReportMonth(1)}
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                  >
                    →
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6">
                {/* INCOME DETAILS SECTION */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t('incomeSectionTitle')}
                  </h3>
                  
                  {/* Assigned Aidat Income - Payments assigned to specific apartments */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('assignedAidatIncomeLabel')}</h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-gray-800">{t('assignedAidatIncomeLabel')}</span>
                          <p className="text-xs text-gray-500 mt-1">{t('assignedAidatIncomeNote')}</p>
                        </div>
                        <span className="font-bold text-green-600 text-lg">{loadingAidatIncome ? '...' : formatCurrency(maintenanceFeeIncome)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Pending Pool Income - Payments not yet assigned to any apartment */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('pendingPoolIncomeLabel')}</h4>
                    <div className={`border rounded-lg p-4 ${poolIncome > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-gray-800">{t('pendingPoolIncomeLabel')}</span>
                          <p className="text-xs text-gray-500 mt-1">{t('pendingPoolIncomeNote')}</p>
                        </div>
                        <span className={`font-bold text-lg ${poolIncome > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {loadingReportSummary ? '...' : formatCurrency(poolIncome)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Other Payment Income - Payments for other fees like "Kapıcı tazminatı" */}
                  {otherPaymentIncome > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('otherPaymentIncomeLabel') || 'Diğer Ödemeler Geliri'}</h4>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-semibold text-gray-800">{t('otherPaymentIncomeLabel') || 'Diğer Ödemeler Geliri'}</span>
                            <p className="text-xs text-gray-500 mt-1">{t('otherPaymentIncomeNote') || 'Kapıcı tazminatı vb. daire ödemeleri'}</p>
                          </div>
                          <span className="font-bold text-blue-600 text-lg">{loadingReportSummary ? '...' : formatCurrency(otherPaymentIncome)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Other Income - Itemized List */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('otherIncomeSectionTitle')}</h4>
                    {reportMonthOtherIncomeItems.length > 0 ? (
                      <div className="space-y-2">
                        {reportMonthOtherIncomeItems.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{translateOtherIncomeCategory(item.category)}</div>
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">{formatDate(item.date)}</p>
                              </div>
                              <span className="font-semibold text-green-600">{formatCurrency(item.amount)}</span>
                            </div>
                          </div>
                        ))}
                        {/* Other Income Total */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex justify-between items-center">
                          <span className="font-semibold text-gray-700">{t('otherIncomeTotalLabel')}</span>
                          <span className="font-bold text-green-600">{formatCurrency(totalOtherIncome)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        {t('noOtherIncomeThisMonth')}
                      </div>
                    )}
                  </div>
                </div>

                {/* EXPENSE DETAILS SECTION */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t('expensesSectionTitle')}
                  </h3>
                  
                  {/* Expenses - Itemized List */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('expenseItemsSectionTitle')}</h4>
                    {reportMonthExpenseItems.length > 0 ? (
                      <div className="space-y-2">
                        {reportMonthExpenseItems.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{translateExpenseCategory(item.category)}</div>
                                {item.subcategory && (
                                  <p className="text-sm text-gray-600">{translateExpenseSubcategory(item.category, item.subcategory)}</p>
                                )}
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">{formatDate(item.date)}</p>
                              </div>
                              <span className="font-semibold text-red-600">{formatCurrency(item.amount)}</span>
                            </div>
                          </div>
                        ))}
                        {/* Expenses Total */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-center">
                          <span className="font-semibold text-gray-700">{t('expensesTotalLabel')}</span>
                          <span className="font-bold text-red-600">{formatCurrency(totalRegularExpenses)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        {t('noExpensesThisMonth')}
                      </div>
                    )}
                  </div>
                  
                  {/* Investments - Itemized List */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('investmentsSectionTitle')}</h4>
                    {reportMonthInvestmentItems.length > 0 ? (
                      <div className="space-y-2">
                        {reportMonthInvestmentItems.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{translateInvestmentCategory(item.category)}</div>
                                {item.subcategory && (
                                  <p className="text-sm text-gray-600">{translateInvestmentSubcategory(item.category, item.subcategory)}</p>
                                )}
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">{formatDate(item.start_date)}</p>
                              </div>
                              <span className="font-semibold text-red-600">
                                {item.amount ? formatCurrency(item.amount) : '—'}
                              </span>
                            </div>
                          </div>
                        ))}
                        {/* Investments Total */}
                        {totalInvestments > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-center">
                            <span className="font-semibold text-gray-700">{t('investmentsTotalLabel')}</span>
                            <span className="font-bold text-red-600">{formatCurrency(totalInvestments)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg text-sm">
                        {t('noInvestmentsThisMonth')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('changePassword')}
              </h2>
              <button 
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                  setPasswordSuccess(false);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 text-xl"
              >
                ×
              </button>
            </div>

            {passwordSuccess ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">✓</div>
                <p className="text-green-600 font-semibold">{t('passwordChangedSuccess')}</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword}>
                {passwordError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {passwordError}
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    {t('currentPassword')}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder={t('enterCurrentPassword')}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    {t('newPassword')}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder={t('enterNewPassword')}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('passwordTooWeak')}</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    {t('confirmNewPassword')}
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder={t('confirmPassword')}
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setPasswordError('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold transition-colors"
                  >
                    {t('save')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Operating Ledger Page
const OperatingLedgerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, formatCurrency } = useI18n();
  const { selectedBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerMonth, setLedgerMonth] = useState(new Date().getMonth() + 1);
  const [ledgerYear, setLedgerYear] = useState(new Date().getFullYear());
  
  // Quick Month/Year Picker state
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date().getMonth() + 1);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  
  // Expense modal state
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState({ 
    category: '', 
    subcategory: '', 
    amount: 0, 
    date: new Date().toISOString().split('T')[0], 
    description: '',
    invoice_no: ''
  });
  const [editingExpense, setEditingExpense] = useState(null); // Track expense being edited
  
  // Past month expense confirmation dialog state
  const [showPastMonthConfirm, setShowPastMonthConfirm] = useState(false);

  // Income modal state (Other Income)
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [incomeFormData, setIncomeFormData] = useState({
    category: 'commonAreaRental',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    invoice_no: ''
  });
  const [editingIncome, setEditingIncome] = useState(null); // Track income being edited

  // Maintenance Payment Edit Modal state
  const [showMaintenanceEditModal, setShowMaintenanceEditModal] = useState(false);
  const [editingMaintenancePayment, setEditingMaintenancePayment] = useState(null);
  const [maintenanceEditApartments, setMaintenanceEditApartments] = useState([]);
  const [selectedNewApartmentId, setSelectedNewApartmentId] = useState('');

  // Pool Income Edit Modal state
  const [showPoolEditModal, setShowPoolEditModal] = useState(false);
  const [editingPoolIncome, setEditingPoolIncome] = useState(null);
  const [poolEditApartments, setPoolEditApartments] = useState([]);
  const [selectedPoolApartmentId, setSelectedPoolApartmentId] = useState('');
  const [poolAssignmentLoading, setPoolAssignmentLoading] = useState(false);

  // Bank Statement Upload Modal state
  const [showBankStatementModal, setShowBankStatementModal] = useState(false);
  const [bankStatementLoading, setBankStatementLoading] = useState(false);
  const [bankStatementResults, setBankStatementResults] = useState(null);
  const [bankStatementError, setBankStatementError] = useState(null);
  const [bankStatementApartments, setBankStatementApartments] = useState([]);
  const [editedTransactions, setEditedTransactions] = useState({}); // Track edited unit assignments
  const [showPostConfirmModal, setShowPostConfirmModal] = useState(false);
  const [postingInProgress, setPostingInProgress] = useState(false);
  const [postingResults, setPostingResults] = useState(null);

  // Manual Maintenance Payment Modal state
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [manualPaymentLoading, setManualPaymentLoading] = useState(false);
  const [manualPaymentApartments, setManualPaymentApartments] = useState([]);
  const [manualPaymentFormData, setManualPaymentFormData] = useState({
    apartment_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });

  // Delete Ledger Entry Modal state
  const [showDeleteLedgerModal, setShowDeleteLedgerModal] = useState(false);
  const [deleteLedgerEntry, setDeleteLedgerEntry] = useState(null);
  const [deleteLedgerLoading, setDeleteLedgerLoading] = useState(false);
  const [deleteLedgerReason, setDeleteLedgerReason] = useState('');

  // Get month name from translations - memoized
  const getMonthName = useCallback((monthIndex) => {
    return t(`months.${monthIndex}`);
  }, [t]);

  // Translate other income category - for displaying in ledger
  const translateOtherIncomeCategory = useCallback((category) => {
    const categoryMap = {
      commonAreaRental: t('commonAreaRental'),
      parkingFees: t('parkingFees'),
      facilityUsageFees: t('facilityUsageFees'),
      penalties: t('penalties'),
      keyCardFees: t('keyCardFees'),
      otherCategory: t('otherCategory')
    };
    return categoryMap[category] || category;
  }, [t]);

  // Get building ID for API calls - memoized
  const getBuildingId = useCallback(() => {
    return user.role === 'superadmin' && selectedBuilding ? selectedBuilding.id : user.building_id;
  }, [user, selectedBuilding]);

  useEffect(() => {
    // Only Admin and SuperAdmin can access
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/app');
      return;
    }
    fetchLedgerData();
  }, [user, navigate, ledgerMonth, ledgerYear, selectedBuilding]);

  const fetchLedgerData = useCallback(async () => {
    setLoading(true);
    try {
      const buildingId = getBuildingId();
      const response = await axios.get(
        `${API}/admin/operating-ledger?month=${ledgerMonth}&year=${ledgerYear}&building_id=${buildingId}`,
        { withCredentials: true }
      );
      setLedgerData(response.data);
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [ledgerMonth, ledgerYear, getBuildingId, t]);

  // Category options for expense modal (same as ExpensesPage) - memoized
  const getCategoryOptions = useMemo(() => [
    { key: 'commonArea', label: t('expenseCategories.commonArea') },
    { key: 'maintenance', label: t('expenseCategories.maintenance') },
    { key: 'management', label: t('expenseCategories.management') },
    { key: 'taxOfficial', label: t('expenseCategories.taxOfficial') },
    { key: 'insurance', label: t('expenseCategories.insurance') },
    { key: 'other', label: t('expenseCategories.other') }
  ], [t]);

  // Subcategory options for expense modal (same as ExpensesPage) - memoized
  const getSubcategoryOptions = useCallback((categoryKey) => {
    if (!categoryKey) return [];
    
    const subcategoriesMap = {
      commonArea: ['cleaning', 'security', 'janitor', 'reception', 'gardening', 'elevatorMaintenance', 'generatorMaintenance', 'fireSystemsMaintenance', 'poolMaintenance', 'commonElectricity', 'commonWater', 'naturalGas'],
      maintenance: ['electricalRepair', 'plumbingRepair', 'elevatorBreakdown', 'roofRepair', 'paintingRenovation', 'fixtureRepair', 'emergencyRepairs'],
      management: ['managerFee', 'accountingAdvisor', 'buildingManagementService', 'legal', 'consultancy', 'stationeryOffice', 'softwareSystem'],
      taxOfficial: ['taxes', 'municipalityFees', 'licenseProcedures', 'penaltiesFees'],
      insurance: ['buildingInsurance', 'assetInsurance', 'occupationalHealth', 'fireExtinguisher'],
      other: ['pestControl', 'wasteManagement', 'snowRemoval', 'generalExpense', 'other']
    };

    const subcategoryKeys = subcategoriesMap[categoryKey] || [];
    return subcategoryKeys.map(key => ({
      key: key,
      label: t(`expenseSubcategories.${categoryKey}.${key}`)
    }));
  }, [t]);

  // Handle opening expense modal with smart date default
  const handleOpenExpenseModal = () => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    
    let defaultDate;
    if (ledgerMonth === currentMonth && ledgerYear === currentYear) {
      // Same month as ledger view - use today
      defaultDate = today.toISOString().split('T')[0];
    } else {
      // Different month - use first day of ledger month
      defaultDate = `${ledgerYear}-${String(ledgerMonth).padStart(2, '0')}-01`;
    }
    
    setExpenseFormData({ 
      category: '', 
      subcategory: '', 
      amount: 0, 
      date: defaultDate, 
      description: '' 
    });
    setShowExpenseModal(true);
  };

  // Handle expense creation (same logic as ExpensesPage)
  // Check if expense date is in a past month compared to ledger month
  const isExpenseDateInPastMonth = () => {
    if (!expenseFormData.date) return false;
    const expenseDate = new Date(expenseFormData.date);
    const expenseMonth = expenseDate.getMonth() + 1; // 1-12
    const expenseYear = expenseDate.getFullYear();
    
    // Compare: if expense date's year/month is before ledger year/month
    if (expenseYear < ledgerYear) return true;
    if (expenseYear === ledgerYear && expenseMonth < ledgerMonth) return true;
    return false;
  };

  // Handle expense form submission with past month check
  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    
    if (!expenseFormData.category) {
      toast.error(t('categoryRequired'));
      return;
    }
    if (!expenseFormData.subcategory) {
      toast.error(t('subcategoryRequired'));
      return;
    }
    
    // Check if date is in a past month - show confirmation if so
    if (isExpenseDateInPastMonth()) {
      setShowPastMonthConfirm(true);
      return;
    }
    
    // Otherwise create directly
    createExpense();
  };

  // Actually create the expense
  const createExpense = async () => {
    try {
      const buildingId = getBuildingId();
      const data = { 
        ...expenseFormData, 
        date: new Date(expenseFormData.date).toISOString(),
        invoice_no: expenseFormData.invoice_no?.trim() || null
      };
      const url = buildingId ? `${API}/expenses?building_id=${buildingId}` : `${API}/expenses`;
      await axios.post(url, data, { withCredentials: true });
      toast.success(t('expenseCreated'));
      setShowExpenseModal(false);
      setShowPastMonthConfirm(false);
      setExpenseFormData({ category: '', subcategory: '', amount: 0, date: new Date().toISOString().split('T')[0], description: '', invoice_no: '' });
      
      // Refresh ledger data to show the new expense
      fetchLedgerData();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  // Handle confirmed past month expense creation
  const handleConfirmPastMonthExpense = () => {
    setShowPastMonthConfirm(false);
    createExpense();
  };

  // Legacy handler for backwards compatibility
  const handleCreateExpense = async (e) => {
    handleExpenseSubmit(e);
  };

  // Handle opening income modal with smart date default
  const handleOpenIncomeModal = () => {
    // Default to first day of the currently selected ledger month
    const defaultDate = `${ledgerYear}-${String(ledgerMonth).padStart(2, '0')}-01`;
    
    setIncomeFormData({
      category: 'commonAreaRental',
      description: '',
      amount: '',
      date: defaultDate
    });
    setShowIncomeModal(true);
  };

  // Handle income creation (same logic as OtherIncomePage)
  const handleCreateIncome = async (e) => {
    e.preventDefault();
    
    if (!incomeFormData.description) {
      toast.error(t('descriptionRequired') || 'Description is required');
      return;
    }
    if (!incomeFormData.amount || parseFloat(incomeFormData.amount) <= 0) {
      toast.error(t('amountRequired') || 'Valid amount is required');
      return;
    }
    
    try {
      const buildingId = getBuildingId();
      const payload = {
        ...incomeFormData,
        amount: parseFloat(incomeFormData.amount)
      };
      const url = buildingId ? `${API}/other-income?building_id=${buildingId}` : `${API}/other-income`;
      await axios.post(url, payload, { withCredentials: true });
      toast.success(t('otherIncomeCreated'));
      setShowIncomeModal(false);
      setIncomeFormData({
        category: 'commonAreaRental',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0]
      });
      
      // Refresh ledger data to show the new income
      fetchLedgerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // Handle opening expense modal for EDITING an existing expense
  const handleEditExpense = (transaction) => {
    // Parse the date from the transaction (format: YYYY-MM-DD)
    const expenseDate = transaction.date;
    
    setEditingExpense(transaction);
    setExpenseFormData({
      category: transaction.category || '',
      subcategory: transaction.subcategory || '',
      amount: Math.abs(transaction.expense) || 0,
      date: expenseDate,
      description: transaction.description || '',
      invoice_no: transaction.invoice_no || ''
    });
    setShowExpenseModal(true);
  };

  // Handle expense UPDATE (when in edit mode)
  const updateExpense = async () => {
    try {
      const buildingId = getBuildingId();
      const data = { 
        ...expenseFormData, 
        date: new Date(expenseFormData.date).toISOString(),
        invoice_no: expenseFormData.invoice_no?.trim() || null
      };
      const url = buildingId 
        ? `${API}/expenses/${editingExpense.id}?building_id=${buildingId}` 
        : `${API}/expenses/${editingExpense.id}`;
      await axios.put(url, data, { withCredentials: true });
      toast.success(t('expenseUpdated'));
      setShowExpenseModal(false);
      setShowPastMonthConfirm(false);
      setEditingExpense(null);
      setExpenseFormData({ category: '', subcategory: '', amount: 0, date: new Date().toISOString().split('T')[0], description: '', invoice_no: '' });
      
      // Refresh ledger data to show the updated expense
      fetchLedgerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // Handle expense submit - CREATE or UPDATE based on mode
  const handleExpenseSubmitOrUpdate = (e) => {
    e.preventDefault();
    
    if (!expenseFormData.category) {
      toast.error(t('categoryRequired'));
      return;
    }
    if (!expenseFormData.subcategory) {
      toast.error(t('subcategoryRequired'));
      return;
    }
    
    // Check if date is in a past month - show confirmation if so
    if (isExpenseDateInPastMonth()) {
      setShowPastMonthConfirm(true);
      return;
    }
    
    // Create or Update based on mode
    if (editingExpense) {
      updateExpense();
    } else {
      createExpense();
    }
  };

  // Handle confirmed past month expense - CREATE or UPDATE
  const handleConfirmPastMonthExpenseAction = () => {
    setShowPastMonthConfirm(false);
    if (editingExpense) {
      updateExpense();
    } else {
      createExpense();
    }
  };

  // Handle closing expense modal - reset edit state
  const handleCloseExpenseModal = () => {
    setShowExpenseModal(false);
    setEditingExpense(null);
    setExpenseFormData({ category: '', subcategory: '', amount: 0, date: new Date().toISOString().split('T')[0], description: '', invoice_no: '' });
  };

  // Handle opening income modal for EDITING an existing income
  const handleEditIncome = (transaction) => {
    setEditingIncome(transaction);
    setIncomeFormData({
      category: transaction.category || 'commonAreaRental',
      description: transaction.description || '',
      amount: Math.abs(transaction.income) || '',
      date: transaction.date,
      invoice_no: transaction.invoice_no || ''
    });
    setShowIncomeModal(true);
  };

  // Handle income CREATE or UPDATE
  const handleCreateOrUpdateIncome = async (e) => {
    e.preventDefault();
    
    if (!incomeFormData.description) {
      toast.error(t('descriptionRequired') || 'Description is required');
      return;
    }
    if (!incomeFormData.amount || parseFloat(incomeFormData.amount) <= 0) {
      toast.error(t('amountRequired') || 'Valid amount is required');
      return;
    }
    
    try {
      const buildingId = getBuildingId();
      const payload = {
        ...incomeFormData,
        amount: parseFloat(incomeFormData.amount),
        invoice_no: incomeFormData.invoice_no?.trim() || null
      };
      
      if (editingIncome) {
        // UPDATE mode
        const url = buildingId 
          ? `${API}/other-income/${editingIncome.id}?building_id=${buildingId}` 
          : `${API}/other-income/${editingIncome.id}`;
        await axios.put(url, payload, { withCredentials: true });
        toast.success(t('otherIncomeUpdated'));
      } else {
        // CREATE mode
        const url = buildingId ? `${API}/other-income?building_id=${buildingId}` : `${API}/other-income`;
        await axios.post(url, payload, { withCredentials: true });
        toast.success(t('otherIncomeCreated'));
      }
      
      handleCloseIncomeModal();
      fetchLedgerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // Handle closing income modal - reset edit state
  const handleCloseIncomeModal = () => {
    setShowIncomeModal(false);
    setEditingIncome(null);
    setIncomeFormData({
      category: 'commonAreaRental',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      invoice_no: ''
    });
  };

  // Handle edit for maintenance payment - opens unit reassignment modal
  const handleEditMaintenancePayment = async (transaction) => {
    setEditingMaintenancePayment(transaction);
    setSelectedNewApartmentId(transaction.apartment_id || '');
    
    // Fetch apartments for selection
    try {
      const buildingId = getBuildingId();
      const url = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      const response = await axios.get(url, { withCredentials: true });
      setMaintenanceEditApartments(response.data.sort((a, b) => 
        (a.unit_number || '').localeCompare(b.unit_number || '', undefined, { numeric: true })
      ));
    } catch (error) {
      console.error('Error fetching apartments:', error);
      toast.error(t('loadDataFailed'));
      return;
    }
    
    setShowMaintenanceEditModal(true);
  };

  // Handle save maintenance payment unit change
  const handleSaveMaintenancePaymentEdit = async () => {
    if (!editingMaintenancePayment?.id || !selectedNewApartmentId) {
      toast.error(t('selectUnit'));
      return;
    }
    
    // Check if same unit selected
    if (editingMaintenancePayment.apartment_id === selectedNewApartmentId) {
      toast.info(t('sameUnitSelected') || 'Same unit selected - no change needed');
      return;
    }
    
    try {
      const buildingId = getBuildingId();
      const url = buildingId 
        ? `${API}/admin/maintenance-payment-transactions/${editingMaintenancePayment.id}?building_id=${buildingId}`
        : `${API}/admin/maintenance-payment-transactions/${editingMaintenancePayment.id}`;
      
      const response = await axios.put(url, { apartment_id: selectedNewApartmentId }, { withCredentials: true });
      
      // Show detailed success message
      const result = response.data;
      const selectedApt = maintenanceEditApartments.find(a => a.id === selectedNewApartmentId);
      toast.success(`${t('paymentReassigned') || 'Payment reassigned'}: ${t('ledgerUnit')} ${selectedApt?.unit_number || '?'} - ${result.new_fees_paid || 0} ${t('feesUpdated') || 'fees updated'}`);
      
      setShowMaintenanceEditModal(false);
      setEditingMaintenancePayment(null);
      setSelectedNewApartmentId('');
      fetchLedgerData();
    } catch (error) {
      console.error('Error updating maintenance payment:', error);
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // Handle close maintenance edit modal
  const handleCloseMaintenanceEditModal = () => {
    setShowMaintenanceEditModal(false);
    setEditingMaintenancePayment(null);
    setSelectedNewApartmentId('');
  };

  // Handle edit for pool income - now allows unit assignment
  const handleEditPoolIncome = async (transaction) => {
    setEditingPoolIncome(transaction);
    setSelectedPoolApartmentId('');  // Reset selection
    
    // Fetch apartments for selection
    try {
      const buildingId = getBuildingId();
      const url = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      const response = await axios.get(url, { withCredentials: true });
      setPoolEditApartments(response.data.sort((a, b) => 
        (a.unit_number || '').localeCompare(b.unit_number || '', undefined, { numeric: true })
      ));
    } catch (error) {
      console.error('Error fetching apartments:', error);
      toast.error(t('loadDataFailed'));
      return;
    }
    
    setShowPoolEditModal(true);
  };

  // Handle save pool income unit assignment
  const handleSavePoolUnitAssignment = async () => {
    if (!editingPoolIncome?.id || !selectedPoolApartmentId) {
      toast.error(t('selectUnit'));
      return;
    }
    
    setPoolAssignmentLoading(true);
    
    try {
      const buildingId = getBuildingId();
      const url = buildingId 
        ? `${API}/admin/pool-income/${editingPoolIncome.id}/assign-unit?building_id=${buildingId}`
        : `${API}/admin/pool-income/${editingPoolIncome.id}/assign-unit`;
      
      const response = await axios.post(url, { apartment_id: selectedPoolApartmentId }, { withCredentials: true });
      
      // Show success with details
      const result = response.data;
      const selectedApt = poolEditApartments.find(a => a.id === selectedPoolApartmentId);
      toast.success(`${t('updateSuccess') || 'Success'}: ${t('ledgerUnit')} ${selectedApt?.unit_number || '?'} - ${result.fees_paid} ${t('feesUpdated') || 'fees updated'}`);
      
      setShowPoolEditModal(false);
      setEditingPoolIncome(null);
      setSelectedPoolApartmentId('');
      fetchLedgerData();  // Refresh ledger to show updated data
    } catch (error) {
      console.error('Error assigning pool income to unit:', error);
      toast.error(error.response?.data?.detail || t('operationFailed'));
    } finally {
      setPoolAssignmentLoading(false);
    }
  };

  // Handle close pool edit modal
  const handleClosePoolEditModal = () => {
    setShowPoolEditModal(false);
    setEditingPoolIncome(null);
    setSelectedPoolApartmentId('');
  };

  // Handle open manual maintenance payment modal
  const handleOpenManualPaymentModal = async () => {
    // Fetch apartments for selection
    try {
      const buildingId = getBuildingId();
      const url = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      const response = await axios.get(url, { withCredentials: true });
      setManualPaymentApartments(response.data.sort((a, b) => 
        (a.unit_number || '').localeCompare(b.unit_number || '', undefined, { numeric: true })
      ));
    } catch (error) {
      console.error('Error fetching apartments:', error);
      toast.error(t('loadDataFailed'));
      return;
    }
    
    // Reset form
    setManualPaymentFormData({
      apartment_id: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
    setShowManualPaymentModal(true);
  };

  // Handle submit manual maintenance payment
  const handleSubmitManualPayment = async () => {
    if (!manualPaymentFormData.apartment_id || !manualPaymentFormData.amount || !manualPaymentFormData.date) {
      toast.error(t('fillAllFields'));
      return;
    }
    
    const amount = parseFloat(manualPaymentFormData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('invalidAmount') || 'Invalid amount');
      return;
    }
    
    setManualPaymentLoading(true);
    
    try {
      const buildingId = getBuildingId();
      const url = buildingId 
        ? `${API}/admin/manual-maintenance-payment?building_id=${buildingId}`
        : `${API}/admin/manual-maintenance-payment`;
      
      const response = await axios.post(url, {
        apartment_id: manualPaymentFormData.apartment_id,
        amount: amount,
        date: manualPaymentFormData.date,
        description: manualPaymentFormData.description || ''
      }, { withCredentials: true });
      
      const result = response.data;
      
      // Build success message
      let successMsg = t('manualPaymentSuccess');
      if (result.fees_paid > 0) {
        successMsg += ` - ${result.fees_paid} ${t('feesApplied')}`;
      }
      if (result.remaining_as_credit > 0) {
        successMsg += `, ${formatCurrency(result.remaining_as_credit)} ${t('remainingAsCredit')}`;
      }
      
      toast.success(successMsg);
      setShowManualPaymentModal(false);
      fetchLedgerData();
    } catch (error) {
      console.error('Error creating manual payment:', error);
      toast.error(error.response?.data?.detail || t('manualPaymentError'));
    } finally {
      setManualPaymentLoading(false);
    }
  };

  // Handle close manual payment modal
  const handleCloseManualPaymentModal = () => {
    setShowManualPaymentModal(false);
    setManualPaymentFormData({
      apartment_id: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
  };

  // Handle opening delete ledger entry modal
  const handleOpenDeleteLedgerModal = (entry) => {
    setDeleteLedgerEntry(entry);
    setDeleteLedgerReason('');
    setShowDeleteLedgerModal(true);
  };

  // Handle delete ledger entry
  const handleDeleteLedgerEntry = async () => {
    if (!deleteLedgerEntry) return;
    
    setDeleteLedgerLoading(true);
    try {
      const buildingId = user?.buildingId;
      const url = buildingId
        ? `${API}/admin/operating-ledger/delete?building_id=${buildingId}`
        : `${API}/admin/operating-ledger/delete`;
      
      const response = await axios.post(url, {
        entry_type: deleteLedgerEntry.type,
        entry_id: deleteLedgerEntry.id,
        void_reason: deleteLedgerReason || 'Deleted from Operating Ledger'
      }, { withCredentials: true });
      
      if (response.data.success) {
        toast.success(t('operatingLedger.deleteSuccess') || 'Entry deleted successfully');
        // Refresh the ledger data
        fetchLedgerData();
        setShowDeleteLedgerModal(false);
        setDeleteLedgerEntry(null);
      }
    } catch (error) {
      console.error('Error deleting ledger entry:', error);
      toast.error(error.response?.data?.detail || t('operatingLedger.deleteError') || 'Failed to delete entry');
    } finally {
      setDeleteLedgerLoading(false);
    }
  };

  // Handle close delete ledger modal
  const handleCloseDeleteLedgerModal = () => {
    setShowDeleteLedgerModal(false);
    setDeleteLedgerEntry(null);
    setDeleteLedgerReason('');
  };

  const changeLedgerMonth = (delta) => {
    let newMonth = ledgerMonth + delta;
    let newYear = ledgerYear;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    setLedgerMonth(newMonth);
    setLedgerYear(newYear);
  };

  // Handle bank statement file upload
  const handleBankStatementUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setBankStatementLoading(true);
    setBankStatementError(null);
    setBankStatementResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const buildingId = getBuildingId();
      const url = buildingId 
        ? `${API}/admin/parse-bank-statement?building_id=${buildingId}`
        : `${API}/admin/parse-bank-statement`;
      
      // Fetch apartments for dropdown
      const apartmentsUrl = buildingId 
        ? `${API}/apartments?building_id=${buildingId}`
        : `${API}/apartments`;
      
      const [parseResponse, apartmentsResponse] = await Promise.all([
        axios.post(url, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }),
        axios.get(apartmentsUrl, { withCredentials: true })
      ]);
      
      setBankStatementResults(parseResponse.data);
      setBankStatementApartments(apartmentsResponse.data.sort((a, b) => 
        (a.unit_number || '').localeCompare(b.unit_number || '', undefined, { numeric: true })
      ));
      setEditedTransactions({}); // Reset edited transactions
      setPostingResults(null); // Reset posting results
    } catch (error) {
      console.error('Error parsing bank statement:', error);
      setBankStatementError(error.response?.data?.detail || t('parseError'));
    } finally {
      setBankStatementLoading(false);
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Handle unit assignment change for bank statement transactions
  const handleTransactionUnitChange = (section, index, apartmentId) => {
    setEditedTransactions(prev => ({
      ...prev,
      [`${section}_${index}`]: apartmentId
    }));
  };

  // Get the apartment ID for a transaction (edited or original)
  const getTransactionApartmentId = (section, index, originalApartmentNumber) => {
    const editKey = `${section}_${index}`;
    if (editedTransactions.hasOwnProperty(editKey)) {
      return editedTransactions[editKey];
    }
    // Find apartment by unit number
    if (originalApartmentNumber) {
      const apt = bankStatementApartments.find(a => a.unit_number === originalApartmentNumber);
      return apt ? apt.id : '';
    }
    return '';
  };

  // Check if a transaction has been edited
  const isTransactionEdited = (section, index) => {
    return editedTransactions.hasOwnProperty(`${section}_${index}`);
  };

  // Get the resident name for a transaction based on selected apartment
  // This dynamically updates when user changes the unit dropdown
  const getTransactionResidentName = (section, index, tx) => {
    const apartmentId = getTransactionApartmentId(section, index, tx.apartment_number);
    
    if (!apartmentId || apartmentId === 'pool') {
      // No unit selected or pool account - show extracted name from description if available
      return tx.resident_name || '-';
    }
    
    // Find the apartment and get resident name
    const apt = bankStatementApartments.find(a => a.id === apartmentId);
    if (apt) {
      // If apartment has multiple residents, show them
      if (apt.residents && apt.residents.length > 1) {
        return apt.residents.map(r => r.name).join(', ');
      }
      return apt.resident_name || '-';
    }
    
    // Fallback to extracted name from description
    return tx.resident_name || '-';
  };

  // Render match type badge for bank statement transactions
  const renderMatchTypeBadge = (tx) => {
    const matchType = tx.match_type;
    const confidence = tx.match_confidence;
    const conflict = tx.conflict_info;
    
    if (!matchType && !conflict) return null;
    
    let badge = null;
    
    switch (matchType) {
      case 'unit_number':
        badge = <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded" title={t('matchedByUnitNumber')}>🏠</span>;
        break;
      case 'full_name':
        badge = <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded" title={`${t('matchedByFullName')}: ${tx.matched_resident_name || ''}`}>👤</span>;
        break;
      case 'similar_name':
        badge = <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded" title={`${t('matchedBySimilarName')}: ${tx.matched_resident_name || ''} (${t('lowConfidence')})`}>≈</span>;
        break;
      case 'full_name_ambiguous':
      case 'similar_name_ambiguous':
        badge = <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded" title={conflict || t('needsReview')}>⚠️</span>;
        break;
      default:
        break;
    }
    
    // Add conflict indicator if present
    const conflictBadge = conflict && matchType === 'unit_number' ? (
      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded ml-1" title={conflict}>⚡</span>
    ) : null;
    
    return (
      <div className="flex items-center gap-1">
        {badge}
        {conflictBadge}
      </div>
    );
  };

  // Handle posting to Operating Ledger
  const handlePostToLedger = async () => {
    setPostingInProgress(true);
    setPostingResults(null);
    
    try {
      const buildingId = getBuildingId();
      
      // Prepare transactions with unit assignments
      const prepareTransactions = (transactions, section) => {
        return transactions.map((tx, idx) => ({
          ...tx,
          assigned_apartment_id: getTransactionApartmentId(section, idx, tx.apartment_number),
          section: section
        }));
      };
      
      const payload = {
        building_id: buildingId,
        aidat_payments: prepareTransactions(bankStatementResults.aidat_payments || [], 'aidat'),
        other_payments: prepareTransactions(bankStatementResults.other_payments || [], 'other'),
        expenses: bankStatementResults.expenses || [],
        unidentified: prepareTransactions(bankStatementResults.unidentified || [], 'unidentified')
      };
      
      const response = await axios.post(
        `${API}/admin/post-bank-statement`,
        payload,
        { withCredentials: true }
      );
      
      setPostingResults(response.data);
      setShowPostConfirmModal(false);
      
      if (response.data.success) {
        toast.success(t('bankStatementPosted'));
        // Refresh ledger data
        fetchLedgerData();
      }
    } catch (error) {
      console.error('Error posting bank statement:', error);
      toast.error(error.response?.data?.detail || t('postingFailed'));
    } finally {
      setPostingInProgress(false);
    }
  };

  // Calculate running balance for each row - memoized
  const calculateRunningBalances = useCallback((transactions) => {
    let runningBalance = 0;
    return transactions.map((tx, index) => {
      runningBalance += tx.income - tx.expense;
      return { ...tx, rowNo: index + 1, runningBalance };
    });
  }, []);

  // Calculate totals - memoized
  const calculateTotals = useCallback((transactions) => {
    return transactions.reduce(
      (acc, tx) => ({
        totalIncome: acc.totalIncome + tx.income,
        totalExpense: acc.totalExpense + tx.expense
      }),
      { totalIncome: 0, totalExpense: 0 }
    );
  }, []);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  // Calculate derived values - must be before early return
  const transactions = ledgerData?.transactions || [];
  const transactionsWithBalance = useMemo(() => calculateRunningBalances(transactions), [transactions, calculateRunningBalances]);
  const totals = useMemo(() => calculateTotals(transactions), [transactions, calculateTotals]);
  const finalBalance = totals.totalIncome - totals.totalExpense;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 py-4 sm:py-8">
      <div className="container mx-auto px-2 sm:px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Back button and Building Name */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/app')} 
                className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                ← {t('back')}
              </button>
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{t('buildingName')}:</span> {ledgerData?.building_name || '-'}
              </div>
            </div>
            
            {/* Title */}
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 order-first sm:order-none" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('operatingLedgerTitle')}
            </h1>
            
            {/* Month Navigation */}
            <div className="flex items-center gap-2 sm:gap-3 bg-gray-100 rounded-full px-3 py-2">
              <button 
                onClick={() => changeLedgerMonth(-1)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white hover:shadow transition-all text-gray-600"
                data-testid="ledger-prev-month"
              >
                ←
              </button>
              <button 
                onClick={() => {
                  setPickerMonth(ledgerMonth);
                  setPickerYear(ledgerYear);
                  setShowMonthPicker(true);
                }}
                className="text-sm font-semibold text-gray-700 min-w-[120px] text-center hover:bg-white hover:shadow px-3 py-1 rounded-full transition-all cursor-pointer"
                data-testid="ledger-month-picker-btn"
              >
                {getMonthName(ledgerMonth)} {ledgerYear}
              </button>
              <button 
                onClick={() => changeLedgerMonth(1)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white hover:shadow transition-all text-gray-600"
                data-testid="ledger-next-month"
              >
                →
              </button>
            </div>
          </div>
          
          {/* Action Buttons Row */}
          <div className="flex justify-end gap-3 mt-4 no-print flex-wrap">
            <button
              onClick={() => window.print()}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-100 transition-colors font-medium"
              data-testid="ledger-print-btn"
            >
              {t('print')}
            </button>
            <button
              onClick={handleOpenManualPaymentModal}
              className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors font-medium"
              data-testid="ledger-add-payment-btn"
            >
              {t('addMaintenancePayment')}
            </button>
            <button
              onClick={handleOpenIncomeModal}
              className="px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors font-medium"
              data-testid="ledger-add-income-btn"
            >
              {t('addIncome')}
            </button>
            <button
              onClick={handleOpenExpenseModal}
              className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors font-medium"
              data-testid="ledger-add-expense-btn"
            >
              {t('addExpense')}
            </button>
          </div>
        </div>

        {/* Ledger Table - Printable Area */}
        <div id="ledger-print-area" className="bg-white rounded-2xl shadow-lg overflow-hidden print-area">
          {/* Print Header - Only visible in print */}
          <div className="print-header hidden print:block p-4 border-b">
            <h1 className="text-xl font-bold text-center">{t('operatingLedgerTitle')}</h1>
            <p className="text-center text-gray-600">{ledgerData?.building_name} - {getMonthName(ledgerMonth)} {ledgerYear}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
                <thead>
                  {/* Main header row */}
                  <tr className="bg-gray-800 text-white">
                    <th colSpan="5" className="border border-gray-600 px-2 py-3 text-center font-bold">
                      {t('ledgerDescription')}
                    </th>
                    <th colSpan="3" className="border border-gray-600 px-2 py-3 text-center font-bold bg-red-700">
                      {t('ledgerExpenses')}
                    </th>
                    <th colSpan="3" className="border border-gray-600 px-2 py-3 text-center font-bold bg-green-700">
                      {t('ledgerIncome')}
                    </th>
                    <th className="border border-gray-600 px-2 py-3 text-center font-bold bg-blue-700">
                      {t('ledgerBalance')}
                    </th>
                    <th className="border border-gray-600 px-2 py-3 text-center font-bold bg-gray-700 print:hidden">
                      {t('actions')}
                    </th>
                  </tr>
                  {/* Sub header row */}
                  <tr className="bg-gray-100 text-gray-700 text-xs">
                    <th className="border border-gray-300 px-2 py-2 text-center w-12">{t('ledgerRowNo')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-center w-24">{t('ledgerDate')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-center w-20">{t('ledgerDocNo')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-center w-20">{t('ledgerInvoiceNo')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-left min-w-[200px]">{t('ledgerDescription')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-28 bg-red-50">{t('ledgerExpenses')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-24 bg-red-50">{t('ledgerGoodsPurchases')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-24 bg-red-50">{t('ledgerPaidVAT')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-28 bg-green-50">{t('ledgerIncome')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-24 bg-green-50">{t('ledgerGoodsSales')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-24 bg-green-50">{t('ledgerCollectedVAT')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-right w-32 bg-blue-50">{t('ledgerTotal')}</th>
                    <th className="border border-gray-300 px-2 py-2 text-center w-20 print:hidden">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactionsWithBalance.map((tx, index) => {
                    // Build localized description based on transaction type
                    let displayDescription = '';
                    
                    if (tx.type === 'devir_banka' || tx.description === 'DEVIR_BANKA') {
                      displayDescription = t('ledgerDevirBanka');
                    } else if (tx.type === 'expense') {
                      // For bank statement imports, show the original description
                      // For manual entries, show category + subcategory
                      if (tx.source === 'bank_statement_import' && tx.description) {
                        displayDescription = tx.description;
                      } else {
                        const categoryLabel = t(`expenseCategories.${tx.category}`) || tx.category;
                        const subcategoryLabel = tx.subcategory ? t(`expenseSubcategories.${tx.category}.${tx.subcategory}`) || tx.subcategory : '';
                        if (subcategoryLabel) {
                          displayDescription = `${categoryLabel} - ${subcategoryLabel}`;
                        } else if (tx.description) {
                          displayDescription = `${categoryLabel} - ${tx.description}`;
                        } else {
                          displayDescription = categoryLabel;
                        }
                      }
                    } else if (tx.type === 'other_income') {
                      // Translate other income category using helper function
                      const incomeCategoryLabel = translateOtherIncomeCategory(tx.category);
                      if (tx.description) {
                        displayDescription = `${incomeCategoryLabel} - ${tx.description}`;
                      } else {
                        displayDescription = incomeCategoryLabel;
                      }
                    } else if (tx.type === 'maintenance_fee') {
                      // Localized maintenance fee collection: "Aidat Tahsilatı - Daire X - M/YYYY"
                      // For multi-period payments (from bank statement), show all covered periods
                      // For bank statement imports with description, show the description
                      if (tx.description && tx.source_tx_id) {
                        // Bank statement import - show original description
                        displayDescription = `${t('ledgerMaintenanceCollection')} - ${t('ledgerUnit')} ${tx.unit_number} - ${tx.description}`;
                      } else if (tx.is_multi_period && tx.periods_covered) {
                        // Multi-period payment - show all periods
                        displayDescription = `${t('ledgerMaintenanceCollection')} - ${t('ledgerUnit')} ${tx.unit_number} - ${tx.periods_covered}`;
                      } else {
                        // Single period payment
                        const baseDesc = `${t('ledgerMaintenanceCollection')} - ${t('ledgerUnit')} ${tx.unit_number} - ${tx.fee_month}/${tx.fee_year}`;
                        displayDescription = tx.is_partial ? `${baseDesc} (${t('partialPaymentLabel')})` : baseDesc;
                      }
                    } else if (tx.type === 'pool_income') {
                      // Pool income - show description with Pool indicator
                      displayDescription = tx.description || t('poolIncome');
                    } else if (tx.type === 'other_payment') {
                      // Other payment transactions - show unit and description
                      const baseDesc = `${t('otherPayment')} - ${t('ledgerUnit')} ${tx.unit_number}`;
                      displayDescription = tx.description ? `${baseDesc} - ${tx.description}` : baseDesc;
                    } else {
                      // Fallback to raw description
                      displayDescription = tx.description || '-';
                    }
                    
                    // Check if this is the Devir Banka row (first row)
                    const isDevirBanka = tx.type === 'devir_banka';
                    
                    // Check document status for badges
                    const isVoid = tx.status === 'void';
                    const isReversal = tx.reversal_of_document_no !== undefined && tx.reversal_of_document_no !== null;
                    
                    // Build row class based on status
                    let rowClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                    if (isDevirBanka) {
                      rowClass = 'bg-yellow-50 font-semibold';
                    } else if (isVoid) {
                      rowClass = 'bg-red-50 line-through opacity-60';
                    } else if (isReversal) {
                      rowClass = 'bg-orange-50';
                    }
                    
                    return (
                      <tr key={index} className={rowClass}>
                        <td className="border border-gray-300 px-2 py-2 text-center">{tx.rowNo}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center">{isDevirBanka ? '-' : formatDate(tx.date)}</td>
                        <td className="border border-gray-300 px-2 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span>{tx.document_no || '-'}</span>
                            {isVoid && (
                              <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded font-medium">
                                {t('documentStatusVoid')}
                              </span>
                            )}
                            {isReversal && (
                              <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded font-medium">
                                {t('documentStatusReversal')}
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Fatura No column - only show for expense/other_income, empty for aidat/devir */}
                        <td className="border border-gray-300 px-2 py-2 text-center text-sm">
                          {(tx.type === 'expense' || tx.type === 'other_income') ? (tx.invoice_no || '-') : '-'}
                        </td>
                        <td className={`border border-gray-300 px-2 py-2 text-left ${isDevirBanka ? 'text-amber-700' : ''}`}>
                          {displayDescription}
                          {isReversal && tx.reversal_of_document_no && (
                            <span className="text-xs text-orange-600 ml-2">
                              ({t('reversalOf')}: {tx.reversal_of_document_no})
                            </span>
                          )}
                          {isVoid && tx.reversed_by_document_no && (
                            <span className="text-xs text-red-600 ml-2">
                              ({t('reversedBy')}: {tx.reversed_by_document_no})
                            </span>
                          )}
                        </td>
                        <td className={`border border-gray-300 px-2 py-2 text-right bg-red-50 font-medium ${tx.expense < 0 ? 'text-green-600' : ''}`}>
                          {tx.expense !== 0 ? formatCurrency(tx.expense) : '-'}
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-right bg-red-50 text-gray-400">0,00</td>
                        <td className="border border-gray-300 px-2 py-2 text-right bg-red-50 text-gray-400">0,00</td>
                        <td className={`border border-gray-300 px-2 py-2 text-right bg-green-50 font-medium ${tx.income < 0 ? 'text-red-600' : ''}`}>
                          {tx.income !== 0 ? formatCurrency(tx.income) : '-'}
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-right bg-green-50 text-gray-400">0,00</td>
                        <td className="border border-gray-300 px-2 py-2 text-right bg-green-50 text-gray-400">0,00</td>
                        <td className={`border border-gray-300 px-2 py-2 text-right font-bold ${
                          tx.runningBalance >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {formatCurrency(tx.runningBalance)}
                        </td>
                        {/* Actions Column - Edit and Delete buttons for all editable rows */}
                        <td className="border border-gray-300 px-2 py-2 text-center print:hidden">
                          <div className="flex gap-1 justify-center flex-wrap">
                            {/* Edit for expenses that are active (not void, not reversal) */}
                            {tx.type === 'expense' && tx.status !== 'void' && !tx.reversal_of_document_no && (
                              <>
                                <button
                                  onClick={() => handleEditExpense(tx)}
                                  className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                                  data-testid={`edit-expense-${tx.document_no}`}
                                >
                                  {t('edit')}
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteLedgerModal(tx)}
                                  className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                                  data-testid={`delete-expense-${tx.document_no}`}
                                >
                                  {t('delete') || 'Sil'}
                                </button>
                              </>
                            )}
                            {/* Edit and Delete for other_income that are active */}
                            {tx.type === 'other_income' && tx.status !== 'void' && !tx.reversal_of_document_no && (
                              <>
                                <button
                                  onClick={() => handleEditIncome(tx)}
                                  className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                                  data-testid={`edit-income-${tx.document_no}`}
                                >
                                  {t('edit')}
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteLedgerModal(tx)}
                                  className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                                  data-testid={`delete-income-${tx.document_no}`}
                                >
                                  {t('delete') || 'Sil'}
                                </button>
                              </>
                            )}
                            {/* Edit and Delete for maintenance_fee */}
                            {tx.type === 'maintenance_fee' && tx.id && (
                              <>
                                <button
                                  onClick={() => handleEditMaintenancePayment(tx)}
                                  className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors"
                                  data-testid={`edit-maintenance-${tx.id}`}
                                >
                                  {t('edit')}
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteLedgerModal(tx)}
                                  className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                                  data-testid={`delete-maintenance-${tx.id}`}
                                >
                                  {t('delete') || 'Sil'}
                                </button>
                              </>
                            )}
                            {/* Edit and Delete for pool_income */}
                            {tx.type === 'pool_income' && tx.id && (
                              <>
                                <button
                                  onClick={() => handleEditPoolIncome(tx)}
                                  className="text-xs px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded transition-colors"
                                  data-testid={`edit-pool-${tx.id}`}
                                >
                                  {t('edit')}
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteLedgerModal(tx)}
                                  className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                                  data-testid={`delete-pool-${tx.id}`}
                                >
                                  {t('delete') || 'Sil'}
                                </button>
                              </>
                            )}
                            {/* Delete for other_payment type */}
                            {tx.type === 'other_payment' && tx.id && (
                              <button
                                onClick={() => handleOpenDeleteLedgerModal(tx)}
                                className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                                data-testid={`delete-other-payment-${tx.id}`}
                              >
                                {t('delete') || 'Sil'}
                              </button>
                            )}
                            {/* No actions for devir_banka, voided, or reversal entries */}
                            {(tx.type === 'devir_banka' || tx.status === 'void' || tx.reversal_of_document_no) && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {/* Grand Total Row */}
                  <tr className="bg-gray-200 font-bold">
                    <td colSpan="5" className="border border-gray-400 px-2 py-3 text-right">
                      {t('ledgerGrandTotal')}
                    </td>
                    <td className="border border-gray-400 px-2 py-3 text-right bg-red-100 text-red-700">
                      {formatCurrency(totals.totalExpense)}
                    </td>
                    <td className="border border-gray-400 px-2 py-3 text-right bg-red-100 text-gray-400">0,00</td>
                    <td className="border border-gray-400 px-2 py-3 text-right bg-red-100 text-gray-400">0,00</td>
                    <td className="border border-gray-400 px-2 py-3 text-right bg-green-100 text-green-700">
                      {formatCurrency(totals.totalIncome)}
                    </td>
                    <td className="border border-gray-400 px-2 py-3 text-right bg-green-100 text-gray-400">0,00</td>
                    <td className="border border-gray-400 px-2 py-3 text-right bg-green-100 text-gray-400">0,00</td>
                    <td className={`border border-gray-400 px-2 py-3 text-right ${
                      finalBalance >= 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {formatCurrency(finalBalance)}
                    </td>
                    <td className="border border-gray-400 px-2 py-3 bg-gray-200 print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
        </div>
      </div>

      {/* Create/Edit Expense Modal - Same as ExpensesPage */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="ledger-expense-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingExpense ? t('editExpense') : t('newExpense')}
            </h2>
            {/* Show document number if editing (immutable) */}
            {editingExpense && editingExpense.document_no && (
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-600">{t('ledgerDocNo')}: </span>
                <span className="font-semibold">{editingExpense.document_no}</span>
                <span className="text-xs text-gray-500 ml-2">({t('documentNoImmutable')})</span>
              </div>
            )}
            <form onSubmit={handleExpenseSubmitOrUpdate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('categoryLabel')}</label>
                <select
                  data-testid="ledger-category-select"
                  value={expenseFormData.category}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, category: e.target.value, subcategory: '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                >
                  <option value="">{t('categoryPlaceholder')}</option>
                  {getCategoryOptions.map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('subcategoryLabel')}</label>
                <select
                  data-testid="ledger-subcategory-select"
                  value={expenseFormData.subcategory}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, subcategory: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  disabled={!expenseFormData.category}
                  required
                >
                  <option value="">{t('subcategoryPlaceholder')}</option>
                  {getSubcategoryOptions(expenseFormData.category).map(subcat => (
                    <option key={subcat.key} value={subcat.key}>{subcat.label}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('amount')}</label>
                <input
                  type="number"
                  data-testid="ledger-expense-amount-input"
                  value={expenseFormData.amount}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('date')}</label>
                <input
                  type="date"
                  data-testid="ledger-date-input"
                  value={expenseFormData.date}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('description')}</label>
                <textarea
                  data-testid="ledger-description-input"
                  value={expenseFormData.description}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('ledgerInvoiceNo')}</label>
                <input
                  type="text"
                  data-testid="ledger-expense-invoice-no-input"
                  value={expenseFormData.invoice_no}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, invoice_no: e.target.value })}
                  placeholder={t('ledgerInvoiceNoPlaceholder')}
                  maxLength={50}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={handleCloseExpenseModal} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button 
                  type="submit" 
                  data-testid="ledger-submit-expense" 
                  disabled={!expenseFormData.category || !expenseFormData.subcategory || !expenseFormData.amount}
                  className={`flex-1 px-4 py-2 rounded-lg ${
                    !expenseFormData.category || !expenseFormData.subcategory || !expenseFormData.amount
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {editingExpense ? t('save') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Income Modal - Same as OtherIncomePage */}
      {showIncomeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="ledger-income-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingIncome ? t('editOtherIncome') : t('newOtherIncome')}
            </h2>
            {/* Show document number if editing (immutable) */}
            {editingIncome && editingIncome.document_no && (
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-600">{t('ledgerDocNo')}: </span>
                <span className="font-semibold">{editingIncome.document_no}</span>
                <span className="text-xs text-gray-500 ml-2">({t('documentNoImmutable')})</span>
              </div>
            )}
            <form onSubmit={handleCreateOrUpdateIncome}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('date')}</label>
                <input
                  type="date"
                  data-testid="ledger-income-date-input"
                  value={incomeFormData.date}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('category')}</label>
                <select
                  data-testid="ledger-income-category-select"
                  value={incomeFormData.category}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, category: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="commonAreaRental">{t('commonAreaRental')}</option>
                  <option value="parkingFees">{t('parkingFees')}</option>
                  <option value="facilityUsageFees">{t('facilityUsageFees')}</option>
                  <option value="penalties">{t('penalties')}</option>
                  <option value="keyCardFees">{t('keyCardFees')}</option>
                  <option value="otherCategory">{t('otherCategory')}</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('description')}</label>
                <textarea
                  data-testid="ledger-income-description-input"
                  value={incomeFormData.description}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('amount')}</label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="ledger-income-amount-input"
                  value={incomeFormData.amount}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('ledgerInvoiceNo')}</label>
                <input
                  type="text"
                  data-testid="ledger-income-invoice-no-input"
                  value={incomeFormData.invoice_no}
                  onChange={(e) => setIncomeFormData({ ...incomeFormData, invoice_no: e.target.value })}
                  placeholder={t('ledgerInvoiceNoPlaceholder')}
                  maxLength={50}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleCloseIncomeModal}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  data-testid="ledger-submit-income"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingIncome ? t('save') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Past Month Expense Confirmation Dialog */}
      {showPastMonthConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="past-month-confirm-dialog">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('pastMonthExpenseTitle')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('pastMonthExpenseMessage')}
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowPastMonthConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="past-month-cancel-btn"
              >
                {t('pastMonthExpenseCancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmPastMonthExpenseAction}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                data-testid="past-month-continue-btn"
              >
                {t('pastMonthExpenseContinue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Payment Edit Modal - Unit Reassignment */}
      {showMaintenanceEditModal && editingMaintenancePayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="maintenance-edit-modal">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('editMaintenancePayment') || 'Edit Maintenance Payment'}
            </h3>
            
            {/* Current Info */}
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">{t('currentUnit') || 'Current Unit'}:</div>
              <div className="font-semibold">{t('ledgerUnit')} {editingMaintenancePayment.unit_number}</div>
              <div className="text-sm text-gray-600 mt-2 mb-1">{t('period') || 'Period'}:</div>
              <div className="font-semibold">{editingMaintenancePayment.fee_month}/{editingMaintenancePayment.fee_year}</div>
              <div className="text-sm text-gray-600 mt-2 mb-1">{t('amount')}:</div>
              <div className="font-semibold text-green-600">{formatCurrency(editingMaintenancePayment.income)}</div>
            </div>
            
            {/* Unit Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">{t('assignToUnit') || 'Assign to Unit'}</label>
              <select
                value={selectedNewApartmentId}
                onChange={(e) => setSelectedNewApartmentId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="maintenance-unit-select"
              >
                <option value="">{t('selectUnit')}</option>
                {maintenanceEditApartments.map(apt => (
                  <option key={apt.id} value={apt.id}>
                    {t('ledgerUnit')} {apt.unit_number} - {apt.resident_name || '-'}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleCloseMaintenanceEditModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="maintenance-edit-cancel"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveMaintenancePaymentEdit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                data-testid="maintenance-edit-save"
              >
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pool Income Edit Modal - Assign to Unit */}
      {showPoolEditModal && editingPoolIncome && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="pool-edit-modal">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('assignPoolToUnit') || 'Assign Pool Income to Unit'}
            </h3>
            
            {/* Pool Income Info */}
            <div className="mb-4 p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full font-medium">
                  {t('poolAccount') || 'Pool Account'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-gray-600">{t('date')}:</div>
                  <div className="font-semibold">{editingPoolIncome.date}</div>
                </div>
                <div>
                  <div className="text-gray-600">{t('amount')}:</div>
                  <div className="font-semibold text-purple-600">{formatCurrency(editingPoolIncome.income)}</div>
                </div>
              </div>
              <div className="mt-2">
                <div className="text-gray-600 text-sm">{t('description')}:</div>
                <div className="font-semibold text-sm">{editingPoolIncome.description || '-'}</div>
              </div>
            </div>
            
            {/* Unit Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('assignToUnit') || 'Assign to Unit'}</label>
              <select
                value={selectedPoolApartmentId}
                onChange={(e) => setSelectedPoolApartmentId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="pool-unit-select"
              >
                <option value="">{t('selectUnit')}</option>
                {poolEditApartments.map(apt => (
                  <option key={apt.id} value={apt.id}>
                    {t('ledgerUnit')} {apt.unit_number} - {apt.resident_name || '-'}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Info Note */}
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                {t('poolAssignmentNote') || 'This will apply the payment to the selected unit\'s oldest unpaid maintenance fees automatically.'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClosePoolEditModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={poolAssignmentLoading}
                data-testid="pool-edit-cancel"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleSavePoolUnitAssignment}
                disabled={!selectedPoolApartmentId || poolAssignmentLoading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pool-edit-save"
              >
                {poolAssignmentLoading ? (t('processing') || 'Processing...') : (t('assignAndApply') || 'Assign & Apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Maintenance Payment Modal */}
      {showManualPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="manual-payment-modal">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('manualPaymentTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('manualPaymentDescription')}
            </p>
            
            {/* Apartment Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('selectApartment')}</label>
              <select
                value={manualPaymentFormData.apartment_id}
                onChange={(e) => setManualPaymentFormData({ ...manualPaymentFormData, apartment_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="manual-payment-apartment"
              >
                <option value="">{t('selectUnit')}</option>
                <option value="pool">{t('poolAccount')}</option>
                {manualPaymentApartments.map(apt => (
                  <option key={apt.id} value={apt.id}>
                    {t('ledgerUnit')} {apt.unit_number} - {apt.resident_name || '-'}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Amount */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('paymentAmount')}</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualPaymentFormData.amount}
                  onChange={(e) => setManualPaymentFormData({ ...manualPaymentFormData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                  placeholder="0.00"
                  data-testid="manual-payment-amount"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">TL</span>
              </div>
            </div>
            
            {/* Date */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('paymentDate')}</label>
              <input
                type="date"
                value={manualPaymentFormData.date}
                onChange={(e) => setManualPaymentFormData({ ...manualPaymentFormData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="manual-payment-date"
              />
            </div>
            
            {/* Description (Optional) */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('paymentDescription')}</label>
              <input
                type="text"
                value={manualPaymentFormData.description}
                onChange={(e) => setManualPaymentFormData({ ...manualPaymentFormData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('paymentDescription')}
                data-testid="manual-payment-description"
              />
            </div>
            
            {/* Info Note */}
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700">
                {t('poolAssignmentNote')}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCloseManualPaymentModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={manualPaymentLoading}
                data-testid="manual-payment-cancel"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmitManualPayment}
                disabled={!manualPaymentFormData.apartment_id || !manualPaymentFormData.amount || manualPaymentLoading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="manual-payment-submit"
              >
                {manualPaymentLoading ? t('processingPayment') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Ledger Entry Confirmation Modal */}
      {showDeleteLedgerModal && deleteLedgerEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-ledger-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('deleteLedgerEntryTitle') || 'Kaydı Sil'}
            </h3>
            
            {/* Warning */}
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                {t('deleteLedgerWarning') || 'Bu işlem geri alınamaz. Tüm ilgili mali veriler güncellenecektir.'}
              </p>
            </div>
            
            {/* Entry Details */}
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('entryType') || 'Tür'}:</span>{' '}
                {deleteLedgerEntry.type === 'expense' ? (t('expense') || 'Gider') :
                 deleteLedgerEntry.type === 'other_income' ? (t('income') || 'Gelir') :
                 deleteLedgerEntry.type === 'maintenance_fee' ? (t('maintenanceFee') || 'Aidat Ödemesi') :
                 deleteLedgerEntry.type === 'pool_income' ? (t('poolIncome') || 'Havuz Geliri') :
                 deleteLedgerEntry.type === 'other_payment' ? (t('otherPayment') || 'Diğer Ödeme') :
                 deleteLedgerEntry.type}
              </p>
              {deleteLedgerEntry.document_no && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{t('documentNo') || 'Belge No'}:</span> {deleteLedgerEntry.document_no}
                </p>
              )}
              <p className="text-sm text-gray-700">
                <span className="font-medium">{t('amount') || 'Tutar'}:</span>{' '}
                {deleteLedgerEntry.income > 0 
                  ? `₺${deleteLedgerEntry.income.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` 
                  : `₺${deleteLedgerEntry.expense.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
              </p>
            </div>
            
            {/* Reason Input (for expenses and other_income which use void) */}
            {(deleteLedgerEntry.type === 'expense' || deleteLedgerEntry.type === 'other_income') && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t('voidReason') || 'Silme Nedeni'} <span className="text-gray-400">({t('optional') || 'İsteğe bağlı'})</span>
                </label>
                <input
                  type="text"
                  value={deleteLedgerReason}
                  onChange={(e) => setDeleteLedgerReason(e.target.value)}
                  placeholder={t('enterVoidReason') || 'Silme nedenini giriniz...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  data-testid="delete-ledger-reason"
                />
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCloseDeleteLedgerModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={deleteLedgerLoading}
                data-testid="delete-ledger-cancel"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteLedgerEntry}
                disabled={deleteLedgerLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="delete-ledger-confirm"
              >
                {deleteLedgerLoading ? (t('deleting') || 'Siliniyor...') : (t('delete') || 'Sil')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Month/Year Picker Modal */}
      {showMonthPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="month-picker-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('selectMonthYear') || 'Select Month/Year'}
            </h3>
            
            {/* Year Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('year') || 'Year'}</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPickerYear(prev => prev - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  ←
                </button>
                <span className="flex-1 text-center text-xl font-bold text-gray-800">{pickerYear}</span>
                <button
                  onClick={() => setPickerYear(prev => prev + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  →
                </button>
              </div>
            </div>
            
            {/* Month Selection Grid */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('month') || 'Month'}</label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                  <button
                    key={month}
                    onClick={() => setPickerMonth(month)}
                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-colors ${
                      pickerMonth === month
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`month-picker-${month}`}
                  >
                    {getMonthName(month).substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Selected Preview */}
            <div className="mb-4 p-3 bg-teal-50 rounded-lg text-center">
              <span className="text-teal-700 font-semibold">
                {getMonthName(pickerMonth)} {pickerYear}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowMonthPicker(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                data-testid="month-picker-cancel"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  setLedgerMonth(pickerMonth);
                  setLedgerYear(pickerYear);
                  setShowMonthPicker(false);
                }}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                data-testid="month-picker-apply"
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Statement Results Modal */}
      {showBankStatementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto my-4" data-testid="bank-statement-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('bankStatementResults')}
              </h2>
              <button
                onClick={() => {
                  setShowBankStatementModal(false);
                  setBankStatementResults(null);
                  setBankStatementError(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Loading State */}
            {bankStatementLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                <p className="text-gray-600">{t('analyzing')}</p>
              </div>
            )}

            {/* Error State */}
            {bankStatementError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700">{bankStatementError}</p>
              </div>
            )}

            {/* Results */}
            {bankStatementResults && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 rounded-xl p-4">
                    <div className="text-sm text-green-700 font-medium">{t('aidatPayments')}</div>
                    <div className="text-2xl font-bold text-green-800">{bankStatementResults.summary.aidat_count}</div>
                    <div className="text-sm text-green-600">{formatCurrency(bankStatementResults.summary.aidat_total)}</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="text-sm text-blue-700 font-medium">{t('otherPaymentsSection')}</div>
                    <div className="text-2xl font-bold text-blue-800">{bankStatementResults.summary.other_payments_count}</div>
                    <div className="text-sm text-blue-600">{formatCurrency(bankStatementResults.summary.other_payments_total)}</div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4">
                    <div className="text-sm text-red-700 font-medium">{t('expensesSection')}</div>
                    <div className="text-2xl font-bold text-red-800">{bankStatementResults.summary.expenses_count}</div>
                    <div className="text-sm text-red-600">{formatCurrency(bankStatementResults.summary.expenses_total)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-sm text-gray-700 font-medium">{t('unidentifiedTransactions')}</div>
                    <div className="text-2xl font-bold text-gray-800">{bankStatementResults.summary.unidentified_count}</div>
                  </div>
                </div>

                {/* Apartments Found */}
                {bankStatementResults.summary.apartments_found?.length > 0 && (
                  <div className="bg-purple-50 rounded-xl p-4">
                    <div className="text-sm text-purple-700 font-medium mb-2">{t('apartmentsFound')}</div>
                    <div className="flex flex-wrap gap-2">
                      {bankStatementResults.summary.apartments_found.map((apt) => (
                        <span key={apt} className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-sm font-medium">
                          {t('ledgerUnit')} {apt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Match Statistics */}
                {bankStatementResults.summary.match_stats && (
                  <div className="bg-indigo-50 rounded-xl p-4">
                    <div className="text-sm text-indigo-700 font-medium mb-2">{t('matchStatistics')}</div>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="px-2 py-1 bg-green-200 text-green-800 rounded">
                        🏠 {t('byUnitNumber')}: {bankStatementResults.summary.match_stats.by_unit_number}
                      </span>
                      <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded">
                        👤 {t('byFullName')}: {bankStatementResults.summary.match_stats.by_full_name}
                      </span>
                      <span className="px-2 py-1 bg-amber-200 text-amber-800 rounded">
                        ≈ {t('bySimilarName')}: {bankStatementResults.summary.match_stats.by_similar_name}
                      </span>
                      {bankStatementResults.summary.match_stats.needs_review > 0 && (
                        <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded">
                          ⚠️ {t('needsReview')}: {bankStatementResults.summary.match_stats.needs_review}
                        </span>
                      )}
                      {bankStatementResults.summary.match_stats.data_conflicts > 0 && (
                        <span className="px-2 py-1 bg-red-200 text-red-800 rounded">
                          ⚡ {t('dataConflicts')}: {bankStatementResults.summary.match_stats.data_conflicts}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Aidat Payments Table */}
                {bankStatementResults.aidat_payments?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-green-800 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      {t('aidatPayments')} ({bankStatementResults.aidat_payments.length})
                    </h3>
                    <div className="overflow-x-auto border rounded-xl">
                      <table className="w-full text-sm">
                        <thead className="bg-green-100">
                          <tr>
                            <th className="px-4 py-2 text-left">{t('ledgerDate')}</th>
                            <th className="px-4 py-2 text-left">{t('assignUnit')}</th>
                            <th className="px-2 py-2 text-center" title={t('matchType')}>🎯</th>
                            <th className="px-4 py-2 text-left">{t('residentName')}</th>
                            <th className="px-4 py-2 text-right">{t('amount')}</th>
                            <th className="px-4 py-2 text-left">{t('ledgerDescription')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bankStatementResults.aidat_payments.map((tx, idx) => (
                            <tr key={idx} className={`hover:bg-green-50 ${isTransactionEdited('aidat', idx) ? 'bg-yellow-50' : ''} ${tx.conflict_info ? 'bg-red-50' : ''}`}>
                              <td className="px-4 py-2">{tx.date}</td>
                              <td className="px-4 py-2">
                                <select
                                  value={getTransactionApartmentId('aidat', idx, tx.apartment_number)}
                                  onChange={(e) => handleTransactionUnitChange('aidat', idx, e.target.value)}
                                  className={`w-full px-2 py-1 border rounded text-sm ${isTransactionEdited('aidat', idx) ? 'border-yellow-500 bg-yellow-50' : tx.conflict_info ? 'border-red-500' : 'border-gray-300'}`}
                                >
                                  <option value="">{t('selectUnit')}</option>
                                  <option value="pool">{t('poolAccount')}</option>
                                  {bankStatementApartments.map(apt => (
                                    <option key={apt.id} value={apt.id}>
                                      {t('ledgerUnit')} {apt.unit_number}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {renderMatchTypeBadge(tx)}
                              </td>
                              <td className="px-4 py-2">{getTransactionResidentName('aidat', idx, tx)}</td>
                              <td className="px-4 py-2 text-right font-semibold text-green-700">
                                {formatCurrency(tx.amount)}
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate" title={tx.description}>
                                {tx.description?.substring(0, 60)}...
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Other Payments Table */}
                {bankStatementResults.other_payments?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                      {t('otherPaymentsSection')} ({bankStatementResults.other_payments.length})
                    </h3>
                    <div className="overflow-x-auto border rounded-xl">
                      <table className="w-full text-sm">
                        <thead className="bg-blue-100">
                          <tr>
                            <th className="px-4 py-2 text-left">{t('ledgerDate')}</th>
                            <th className="px-4 py-2 text-left">{t('assignUnit')}</th>
                            <th className="px-2 py-2 text-center" title={t('matchType')}>🎯</th>
                            <th className="px-4 py-2 text-left">{t('residentName')}</th>
                            <th className="px-4 py-2 text-right">{t('amount')}</th>
                            <th className="px-4 py-2 text-left">{t('ledgerDescription')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bankStatementResults.other_payments.map((tx, idx) => (
                            <tr key={idx} className={`hover:bg-blue-50 ${isTransactionEdited('other', idx) ? 'bg-yellow-50' : ''} ${tx.conflict_info ? 'bg-red-50' : ''}`}>
                              <td className="px-4 py-2">{tx.date}</td>
                              <td className="px-4 py-2">
                                <select
                                  value={getTransactionApartmentId('other', idx, tx.apartment_number)}
                                  onChange={(e) => handleTransactionUnitChange('other', idx, e.target.value)}
                                  className={`w-full px-2 py-1 border rounded text-sm ${isTransactionEdited('other', idx) ? 'border-yellow-500 bg-yellow-50' : tx.conflict_info ? 'border-red-500' : 'border-gray-300'}`}
                                >
                                  <option value="">{t('selectUnit')}</option>
                                  <option value="pool">{t('poolAccount')}</option>
                                  {bankStatementApartments.map(apt => (
                                    <option key={apt.id} value={apt.id}>
                                      {t('ledgerUnit')} {apt.unit_number}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {renderMatchTypeBadge(tx)}
                              </td>
                              <td className="px-4 py-2">{getTransactionResidentName('other', idx, tx)}</td>
                              <td className="px-4 py-2 text-right font-semibold text-blue-700">
                                {formatCurrency(tx.amount)}
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate" title={tx.description}>
                                {tx.description?.substring(0, 60)}...
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Expenses Table */}
                {bankStatementResults.expenses?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      {t('expensesSection')} ({bankStatementResults.expenses.length})
                    </h3>
                    <div className="overflow-x-auto border rounded-xl">
                      <table className="w-full text-sm">
                        <thead className="bg-red-100">
                          <tr>
                            <th className="px-4 py-2 text-left">{t('ledgerDate')}</th>
                            <th className="px-4 py-2 text-left">{t('category')}</th>
                            <th className="px-4 py-2 text-left">{t('subcategory')}</th>
                            <th className="px-4 py-2 text-right">{t('amount')}</th>
                            <th className="px-4 py-2 text-left">{t('ledgerDescription')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bankStatementResults.expenses.map((tx, idx) => {
                            // Translate category and subcategory using i18n
                            const categoryLabel = t(`expenseCategories.${tx.category}`) !== `expenseCategories.${tx.category}` 
                              ? t(`expenseCategories.${tx.category}`) 
                              : tx.category;
                            const subcategoryLabel = tx.subcategory && t(`expenseSubcategories.${tx.category}.${tx.subcategory}`) !== `expenseSubcategories.${tx.category}.${tx.subcategory}`
                              ? t(`expenseSubcategories.${tx.category}.${tx.subcategory}`)
                              : tx.subcategory || '-';
                            
                            return (
                              <tr key={idx} className="hover:bg-red-50">
                                <td className="px-4 py-2">{tx.date}</td>
                                <td className="px-4 py-2 font-medium">{categoryLabel}</td>
                                <td className="px-4 py-2 text-gray-700">{subcategoryLabel}</td>
                                <td className="px-4 py-2 text-right font-semibold text-red-700">
                                  {formatCurrency(tx.amount)}
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate" title={tx.description}>
                                  {tx.description?.substring(0, 80)}...
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Unidentified Transactions */}
                {bankStatementResults.unidentified?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                      {t('unidentifiedTransactions')} ({bankStatementResults.unidentified.length})
                    </h3>
                    <div className="overflow-x-auto border rounded-xl">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">{t('ledgerDate')}</th>
                            <th className="px-4 py-2 text-left">{t('assignUnit')}</th>
                            <th className="px-2 py-2 text-center" title={t('matchType')}>🎯</th>
                            <th className="px-4 py-2 text-left">{t('residentName')}</th>
                            <th className="px-4 py-2 text-right">{t('amount')}</th>
                            <th className="px-4 py-2 text-left">{t('ledgerDescription')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bankStatementResults.unidentified.map((tx, idx) => (
                            <tr key={idx} className={`hover:bg-gray-50 ${isTransactionEdited('unidentified', idx) ? 'bg-yellow-50' : ''} ${tx.conflict_info ? 'bg-red-50' : ''}`}>
                              <td className="px-4 py-2">{tx.date}</td>
                              <td className="px-4 py-2">
                                <select
                                  value={getTransactionApartmentId('unidentified', idx, tx.apartment_number)}
                                  onChange={(e) => handleTransactionUnitChange('unidentified', idx, e.target.value)}
                                  className={`w-full px-2 py-1 border rounded text-sm ${isTransactionEdited('unidentified', idx) ? 'border-yellow-500 bg-yellow-50' : tx.conflict_info ? 'border-red-500' : 'border-gray-300'}`}
                                >
                                  <option value="">{t('selectUnit')}</option>
                                  <option value="pool">{t('poolAccount')}</option>
                                  {bankStatementApartments.map(apt => (
                                    <option key={apt.id} value={apt.id}>
                                      {t('ledgerUnit')} {apt.unit_number}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {renderMatchTypeBadge(tx)}
                              </td>
                              <td className="px-4 py-2">{getTransactionResidentName('unidentified', idx, tx)}</td>
                              <td className="px-4 py-2 text-right font-semibold">
                                {formatCurrency(tx.amount)}
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate" title={tx.description}>
                                {tx.description?.substring(0, 100)}...
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* No Results State */}
                {bankStatementResults.summary.aidat_count === 0 && 
                 bankStatementResults.summary.other_payments_count === 0 && 
                 bankStatementResults.summary.expenses_count === 0 && 
                 bankStatementResults.summary.unidentified_count === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {t('noTransactionsFound')}
                  </div>
                )}

                {/* Posting Results */}
                {postingResults && (
                  <div className={`rounded-xl p-4 ${postingResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <h4 className={`font-bold mb-3 ${postingResults.success ? 'text-green-800' : 'text-red-800'}`}>
                      {postingResults.success ? t('postingComplete') : t('postingFailed')}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">{t('transactionsProcessed')}:</span>
                        <span className="ml-2 font-semibold">{postingResults.processed_count || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('assignedToUnits')}:</span>
                        <span className="ml-2 font-semibold">{postingResults.unit_payments || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('assignedToPool')}:</span>
                        <span className="ml-2 font-semibold">{postingResults.pool_payments || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('expensesPosted')}:</span>
                        <span className="ml-2 font-semibold">{postingResults.expenses_posted || 0}</span>
                      </div>
                    </div>
                    {postingResults.failed_count > 0 && (
                      <div className="mt-3 text-red-700">
                        <span className="font-semibold">{t('failedTransactions')}:</span> {postingResults.failed_count}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons - Fixed at Bottom */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowBankStatementModal(false);
                  setBankStatementResults(null);
                  setBankStatementError(null);
                  setEditedTransactions({});
                  setPostingResults(null);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors"
              >
                {t('close')}
              </button>
              
              {bankStatementResults && !postingResults?.success && (
                <button
                  onClick={() => setShowPostConfirmModal(true)}
                  disabled={postingInProgress}
                  className="px-6 py-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="post-to-ledger-btn"
                >
                  {postingInProgress ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('posting')}
                    </>
                  ) : (
                    t('postToOperatingLedger')
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post Confirmation Modal */}
      {showPostConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="post-confirm-modal">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('confirmPostTitle')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('confirmPostMessage')}
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-gray-600">{t('aidatPayments')}:</span>
                <span className="font-semibold">{bankStatementResults?.aidat_payments?.length || 0}</span>
                <span className="text-gray-600">{t('otherPaymentsSection')}:</span>
                <span className="font-semibold">{bankStatementResults?.other_payments?.length || 0}</span>
                <span className="text-gray-600">{t('expensesSection')}:</span>
                <span className="font-semibold">{bankStatementResults?.expenses?.length || 0}</span>
                <span className="text-gray-600">{t('unidentifiedTransactions')}:</span>
                <span className="font-semibold">{bankStatementResults?.unidentified?.length || 0}</span>
              </div>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowPostConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handlePostToLedger}
                disabled={postingInProgress}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {postingInProgress ? t('posting') : t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, fetchSettings, language: currentLanguage, currency: currentCurrency } = useI18n();
  const [tempLanguage, setTempLanguage] = useState('tr');
  const [tempCurrency, setTempCurrency] = useState('TRY');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // All authenticated users can access settings
    if (!user) {
      navigate('/app');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setTempLanguage(response.data.language || 'tr');
      setTempCurrency(response.data.currency || 'TRY');
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {};
      
      // Everyone can update their language
      if (tempLanguage !== currentLanguage) {
        payload.language = tempLanguage;
      }
      
      // Only SuperAdmin can update currency
      if (tempCurrency !== currentCurrency) {
        if (user.role !== 'superadmin') {
          toast.error(t('onlySuperAdminCanChangeCurrency'));
          return;
        }
        payload.currency = tempCurrency;
      }
      
      if (Object.keys(payload).length === 0) {
        toast.info(t('noChangesToSave'));
        return;
      }
      
      await axios.put(`${API}/settings`, payload, { withCredentials: true });
      toast.success(t('settingsUpdated'));
      
      // Refresh settings in context
      await fetchSettings();
      
      // Reload the page to apply language changes immediately
      if (payload.language) {
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('onlySuperAdminCanChangeCurrency'));
      } else {
        toast.error(t('operationFailed'));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('settings')}</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2">{t('language')}</label>
              <select
                value={tempLanguage}
                onChange={(e) => setTempLanguage(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="en">{t('english')}</option>
                <option value="tr">{t('turkish')}</option>
                <option value="az">{t('azerbaijani')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">{t('personalLanguageSetting')}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">{t('currency')}</label>
              <select
                value={tempCurrency}
                onChange={(e) => setTempCurrency(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                disabled={user?.role !== 'superadmin'}
              >
                <option value="USD">USD ($)</option>
                <option value="TRY">TRY (₺)</option>
                <option value="AZN">AZN (₼)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {user?.role === 'superadmin' ? t('systemCurrencySuperAdmin') : t('systemCurrencyReadOnly')}
              </p>
            </div>
            <button
              onClick={handleSave}
              className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              {t('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Users Management Page
const UsersPage = ({ buildingId = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({ username: '', password: '', name: '', email: '', phone: '', role: 'resident', apartment_id: '' });
  const [editFormData, setEditFormData] = useState({ name: '', email: '', phone: '', apartment_id: '' });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
      navigate('/app');
      return;
    }
    fetchUsers();
  }, [user, navigate, buildingId]);

  const fetchUsers = async () => {
    try {
      const usersUrl = buildingId ? `${API}/users?building_id=${buildingId}` : `${API}/users`;
      const apartmentsUrl = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      
      const [usersRes, apartmentsRes] = await Promise.all([
        axios.get(usersUrl, { withCredentials: true }),
        axios.get(apartmentsUrl, { withCredentials: true })
      ]);
      
      setUsers(usersRes.data);
      setApartments(apartmentsRes.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate apartment selection - required ONLY for residents
    if (formData.role === 'resident' && !formData.apartment_id) {
      toast.error(t('apartmentRequiredForResident'));
      return;
    }
    
    try {
      const url = buildingId ? `${API}/users/create?building_id=${buildingId}` : `${API}/users/create`;
      await axios.post(url, formData, { withCredentials: true });
      toast.success(t('userCreated'));
      setShowModal(false);
      setFormData({ username: '', password: '', name: '', email: '', phone: '', role: 'resident', apartment_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditFormData({ 
      name: user.name || '', 
      email: user.email || '', 
      phone: user.phone || '',
      apartment_id: user.apartment_id || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Validate apartment selection - required ONLY for residents
    if (selectedUser.role === 'resident' && !editFormData.apartment_id) {
      toast.error(t('apartmentRequiredForResident'));
      return;
    }
    
    try {
      const url = buildingId 
        ? `${API}/users/${selectedUser._id}?building_id=${buildingId}`
        : `${API}/users/${selectedUser._id}`;
      await axios.put(url, editFormData, { withCredentials: true });
      toast.success(t('userUpdated'));
      setShowEditModal(false);
      setSelectedUser(null);
      setEditFormData({ name: '', email: '', phone: '', apartment_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }
    try {
      const url = buildingId 
        ? `${API}/users/${selectedUser._id}/reset-password?building_id=${buildingId}`
        : `${API}/users/${selectedUser._id}/reset-password`;
      await axios.put(url, 
        { new_password: newPassword }, 
        { withCredentials: true }
      );
      toast.success(t('passwordResetSuccess'));
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('users')}</h1>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
            >
              {t('newUser')}
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('username')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('email')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('phone')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('role')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('apartmentNo')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((u) => {
                  // Find apartment for this user
                  const userApartment = apartments.find(apt => apt.id === u.apartment_id);
                  const apartmentDisplay = userApartment ? userApartment.unit_number : t('noApartment');
                  
                  // Get localized role label
                  const roleLabel = u.role === 'admin' ? t('admin') : t('residentRole');
                  
                  return (
                    <tr key={u._id}>
                      <td className="px-6 py-4 text-sm">{u.name}</td>
                      <td className="px-6 py-4 text-sm">{u.username || u.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.email || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.phone || '-'}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {roleLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{apartmentDisplay}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(u)}
                            className="text-teal-600 hover:text-teal-700 font-semibold"
                          >
                            {t('edit')}
                          </button>
                          {u.auth_type === 'manual' && (
                            <button
                              onClick={() => handleResetPassword(u)}
                              className="text-indigo-600 hover:text-indigo-700 font-semibold"
                            >
                              {t('resetPassword')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('newUser')}</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('username')}</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('password')}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  minLength="6"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('emailOptional')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder={t('emailOptional')}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('phoneOptional')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder={t('phoneOptional')}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('role')}</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="resident">{t('residentRole')}</option>
                  <option value="admin">{t('admin')}</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  {t('apartment')} {formData.role === 'resident' ? '*' : `(${t('optional')})`}
                </label>
                <select
                  value={formData.apartment_id}
                  onChange={(e) => setFormData({ ...formData, apartment_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required={formData.role === 'resident'}
                >
                  <option value="">{t('selectApartment')}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {t('unitNumber')} {apt.unit_number}
                    </option>
                  ))}
                </select>
                {formData.role === 'resident' && (
                  <p className="text-xs text-gray-500 mt-1">{t('apartmentRequiredForResident')}</p>
                )}
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg">{t('create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('editUser')}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('username')}: <strong>{selectedUser.username}</strong>
            </p>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('name')}</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('emailOptional')}</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder={t('emailOptional')}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('phoneOptional')}</label>
                <input
                  type="tel"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder={t('phoneOptional')}
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  {t('apartment')} {selectedUser.role === 'resident' ? '*' : `(${t('optional')})`}
                </label>
                <select
                  value={editFormData.apartment_id}
                  onChange={(e) => setEditFormData({ ...editFormData, apartment_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required={selectedUser.role === 'resident'}
                >
                  <option value="">{t('selectApartment')}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {t('unitNumber')} {apt.unit_number}
                    </option>
                  ))}
                </select>
                {selectedUser.role === 'resident' && (
                  <p className="text-xs text-gray-500 mt-1">{t('apartmentRequiredForResident')}</p>
                )}
              </div>
              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    setEditFormData({ name: '', email: '', phone: '', apartment_id: '' });
                  }} 
                  className="flex-1 px-4 py-2 border rounded-lg"
                >
                  {t('cancel')}
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('resetPassword')}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('resetPasswordFor')}: <strong>{selectedUser.name}</strong> ({selectedUser.username})
            </p>
            <form onSubmit={handleResetPasswordSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  minLength="6"
                  placeholder={t('enterNewPassword')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('passwordMinLength')}</p>
              </div>
              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }} 
                  className="flex-1 px-4 py-2 border rounded-lg"
                >
                  {t('cancel')}
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  {t('resetPassword')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


// Import page components
import { ApartmentsPage as ApartmentsPageComponent } from './pages/ApartmentsPage';
import { FeesPage as FeesPageComponent } from './pages/FeesPage';
import { ExpensesPage as ExpensesPageComponent } from './pages/ExpensesPage';
import { AnnouncementsPage as AnnouncementsPageComponent } from './pages/AnnouncementsPage';
import { InvestmentsPage as InvestmentsPageComponent } from './pages/InvestmentsPage';
import { OtherIncomePage as OtherIncomePageComponent } from './pages/OtherIncomePage';
import { BuildingsPage as BuildingsPageComponent } from './pages/BuildingsPage';
import { BankStatementsPage as BankStatementsPageComponent } from './pages/BankStatementsPage';
import { WhatsAppPage as WhatsAppPageComponent } from './pages/WhatsAppPage';
import { SupportPage } from './pages/SupportPage';
import { PrivacyPage } from './pages/PrivacyPage';

// Wrapper components to pass i18n props
const ApartmentsPageWrapper = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <ApartmentsPageComponent user={user} t={t} buildingId={buildingId} />;
};

const FeesPageWrapper = () => {
  const { user } = useAuth();
  const { t, formatCurrency } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <FeesPageComponent user={user} t={t} formatCurrency={formatCurrency} buildingId={buildingId} />;
};

const ExpensesPageWrapper = () => {
  const { user } = useAuth();
  const { t, formatCurrency } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <ExpensesPageComponent user={user} t={t} formatCurrency={formatCurrency} buildingId={buildingId} />;
};

const AnnouncementsPageWrapper = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <AnnouncementsPageComponent user={user} t={t} buildingId={buildingId} />;
};

const InvestmentsPageWrapper = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <InvestmentsPageComponent user={user} t={t} buildingId={buildingId} />;
};

const OtherIncomePageWrapper = () => {
  const { user } = useAuth();
  const { t, formatCurrency } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <OtherIncomePageComponent user={user} t={t} formatCurrency={formatCurrency} buildingId={buildingId} />;
};

const UsersPageWrapper = () => {
  const { user } = useAuth();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <UsersPage buildingId={buildingId} />;
};

const BuildingsPageWrapper = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  return <BuildingsPageComponent user={user} t={t} />;
};

const BankStatementsPageWrapper = () => {
  const { user } = useAuth();
  const { t, formatCurrency } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <BankStatementsPageComponent user={user} t={t} formatCurrency={formatCurrency} buildingId={buildingId} />;
};

const WhatsAppPageWrapper = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { selectedBuilding, viewMode } = useBuilding();
  const buildingId = (user.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) ? selectedBuilding.id : null;
  return <WhatsAppPageComponent user={user} t={t} buildingId={buildingId} />;
};

// Inner wrapper to provide I18n with building context
const AppWithI18nAndBuilding = () => {
  const { user } = useAuth();
  const { selectedBuilding, viewMode } = useBuilding();
  
  // Calculate effective building for language
  const effectiveBuilding = (user?.role === 'superadmin' && viewMode === 'admin' && selectedBuilding) 
    ? selectedBuilding 
    : null;
  
  return (
    <I18nProvider user={user} selectedBuilding={effectiveBuilding} viewMode={viewMode}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/dashboard" element={<SessionHandler />} />
            <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/operating-ledger" element={<ProtectedRoute><OperatingLedgerPage /></ProtectedRoute>} />
            <Route path="/bank-statements" element={<ProtectedRoute><BankStatementsPageWrapper /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UsersPageWrapper /></ProtectedRoute>} />
            <Route path="/apartments" element={<ProtectedRoute><ApartmentsPageWrapper /></ProtectedRoute>} />
            <Route path="/fees" element={<ProtectedRoute><FeesPageWrapper /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><ExpensesPageWrapper /></ProtectedRoute>} />
            <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPageWrapper /></ProtectedRoute>} />
            <Route path="/investments" element={<ProtectedRoute><InvestmentsPageWrapper /></ProtectedRoute>} />
            <Route path="/other-income" element={<ProtectedRoute><OtherIncomePageWrapper /></ProtectedRoute>} />
            <Route path="/buildings" element={<ProtectedRoute><BuildingsPageWrapper /></ProtectedRoute>} />
            <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppPageWrapper /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </I18nProvider>
  );
};

// Wrapper to provide I18n with user context
const AppWithI18n = () => {
  const { user } = useAuth();
  return (
    <BuildingProvider>
      <AppWithI18nAndBuilding />
    </BuildingProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppWithI18n />
    </AuthProvider>
  );
}

export default App;